import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Flame, ChevronDown, Sparkles } from 'lucide-react';
export function NewHero() {
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black" data-id="element-1412">
      {/* Enhanced atmospheric background with more depth */}
      <div className="absolute inset-0 bg-gradient-radial from-stone-900 via-black to-black" data-id="element-1413" />

      {/* Larger, more dramatic candlelight glow spots */}
      <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-amber-600/15 rounded-full blur-3xl animate-flicker" data-id="element-1414" />
      <div className="absolute bottom-1/3 right-1/4 w-[600px] h-[600px] bg-amber-500/15 rounded-full blur-3xl animate-flicker-delayed" data-id="element-1415" />

      {/* Additional ambient glow for depth */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-amber-600/5 rounded-full blur-3xl animate-pulse-slow" data-id="element-1416" />

      {/* Vignette for focus */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60 pointer-events-none" data-id="element-1417" />

      {/* MONUMENTAL BACKGROUND LOGO - Your actual PSI logo as powerful presence */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 3,
      delay: 0.5
    }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{
      width: '100vh',
      height: '100vh' // Mobile default
    }} data-id="element-1418">
        {/* Responsive width override */}
        <style data-id="element-1419">{`
        @media (min-width: 768px) {
          .hero-logo-container {
            width: 180vh !important;
            height: 180vh !important;
          }
        }
      `}</style>
        {/* PSI Logo - Rotating slowly with perfect opacity */}
        <motion.div className="w-full h-full flex items-center justify-center hero-logo-container" animate={{
        rotate: [0, 360]
      }} transition={{
        duration: 180,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-1420">
          <img src="https://cdn.magicpatterns.com/uploads/jToXSpcV6pHGAAwW9hQchh/PeptideScienceInstituteLogo2-Transparent.png" alt="PSI Logo" className="w-full h-full object-contain" style={{
          opacity: 0.06,
          filter: 'blur(1px)'
        }} data-id="element-1421" />
        </motion.div>

        {/* Subtle glow effect around the logo */}
        <motion.div className="absolute inset-0 bg-gradient-radial from-purple-600/8 via-cyan-600/5 to-transparent blur-3xl" animate={{
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-1422" />
      </motion.div>

      {/* Enhanced floating candles - more of them with varied timing */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 2,
      delay: 0.8
    }} className="absolute top-32 left-16 hidden lg:flex flex-col items-center" data-id="element-1423">
        <Flame className="w-7 h-7 text-amber-500 animate-flicker" data-id="element-1424" />
        <div className="w-1 h-20 bg-gradient-to-b from-amber-900/60 to-transparent" data-id="element-1425" />
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 2,
      delay: 1.0
    }} className="absolute top-32 right-16 hidden lg:flex flex-col items-center" data-id="element-1426">
        <Flame className="w-7 h-7 text-amber-500 animate-flicker-delayed" data-id="element-1427" />
        <div className="w-1 h-20 bg-gradient-to-b from-amber-900/60 to-transparent" data-id="element-1428" />
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 2,
      delay: 0.9
    }} className="absolute top-1/3 left-32 hidden xl:flex flex-col items-center" data-id="element-1429">
        <Flame className="w-6 h-6 text-amber-500 animate-flicker" style={{
        animationDelay: '0.3s'
      }} data-id="element-1430" />
        <div className="w-1 h-16 bg-gradient-to-b from-amber-900/50 to-transparent" data-id="element-1431" />
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 2,
      delay: 1.1
    }} className="absolute top-1/3 right-32 hidden xl:flex flex-col items-center" data-id="element-1432">
        <Flame className="w-6 h-6 text-amber-500 animate-flicker-delayed" style={{
        animationDelay: '0.5s'
      }} data-id="element-1433" />
        <div className="w-1 h-16 bg-gradient-to-b from-amber-900/50 to-transparent" data-id="element-1434" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center py-12 sm:py-16 md:py-20" data-id="element-1435">
        {/* Trust badge text above pre-headline */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.3,
        type: 'spring'
      }} className="flex justify-center mb-4" data-id="element-1436">
          <div className="relative px-4 py-2 border border-stone-500/40 rounded-md" data-id="element-1437">
            <p className="relative text-xs sm:text-sm tracking-[0.15em] uppercase font-medium text-stone-300" style={{
            fontFamily: 'system-ui, -apple-system, sans-serif',
            letterSpacing: '0.15em'
          }} data-id="element-1438">
              Trusted by Over 1,000+ Doctors, Biohackers, Researchers, Coaches,
              Health Enthusiasts & Professionals
            </p>
          </div>
        </motion.div>

        {/* Main headline - DRAMATICALLY ENHANCED */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.5
      }} className="mb-8 sm:mb-10" data-id="element-1439">
          <motion.h1 initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 1.2,
          type: 'spring',
          stiffness: 100
        }} className="font-bold leading-tight" style={{
          fontFamily: 'Cinzel, serif'
        }} data-id="element-1440">
            {/* Line 1: Scientifically PROVING - intro text */}
            <motion.div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-4 md:mb-6" style={{
            color: '#fbbf24'
          }} data-id="element-1441">
              <span className="inline-block bg-gradient-to-r from-amber-400 via-amber-200 to-stone-200 bg-clip-text text-transparent" style={{
              backgroundSize: '200% 100%',
              textShadow: '0 0 60px rgba(217, 119, 6, 0.3), 0 0 30px rgba(217, 119, 6, 0.2)',
              filter: 'drop-shadow(0 0 20px rgba(251, 191, 36, 0.25))'
            }} data-id="element-1442">
                Scientifically PROVING
              </span>
            </motion.div>

            {/* Line 2: Life-Changing Magic of Peptides - LARGEST with boxes */}
            <motion.div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl mb-4 md:mb-6" data-id="element-1443">
              <span className="relative inline-block px-8 py-3 mx-3" data-id="element-1444">
                {/* Corner ornaments */}
                <span className="absolute -top-1 -left-1 w-5 h-5 border-l-2 border-t-2 border-amber-400/70 z-0" style={{
                boxShadow: '0 0 8px rgba(251, 191, 36, 0.3)'
              }} data-id="element-1445" />
                <span className="absolute -top-1 -right-1 w-5 h-5 border-r-2 border-t-2 border-amber-400/70 z-0" style={{
                boxShadow: '0 0 8px rgba(251, 191, 36, 0.3)'
              }} data-id="element-1446" />
                <span className="absolute -bottom-1 -left-1 w-5 h-5 border-l-2 border-b-2 border-amber-400/70 z-0" style={{
                boxShadow: '0 0 8px rgba(251, 191, 36, 0.3)'
              }} data-id="element-1447" />
                <span className="absolute -bottom-1 -right-1 w-5 h-5 border-r-2 border-b-2 border-amber-400/70 z-0" style={{
                boxShadow: '0 0 8px rgba(251, 191, 36, 0.3)'
              }} data-id="element-1448" />

                <span className="relative z-10" style={{
                color: '#fbbf24',
                textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
              }} data-id="element-1449">
                  The Life-Changing
                  <br data-id="element-1450" />
                  Magic of Peptides
                </span>
              </span>
            </motion.div>

            {/* Line 3: LIVE - electric standalone emphasis */}
            <motion.div className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl mt-4 md:mt-6 mb-3 md:mb-5" animate={{
            textShadow: ['0 0 20px rgba(34, 211, 238, 0.4), 0 0 60px rgba(34, 211, 238, 0.2)', '0 0 40px rgba(34, 211, 238, 0.7), 0 0 80px rgba(34, 211, 238, 0.4)', '0 0 20px rgba(34, 211, 238, 0.4), 0 0 60px rgba(34, 211, 238, 0.2)']
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-1451">
              <span className="relative inline-block font-extrabold tracking-wider" style={{
              color: '#22d3ee',
              letterSpacing: '0.15em'
            }} data-id="element-1452">
                LIVE
                <span className="absolute -bottom-1 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent" data-id="element-1453" />
              </span>
            </motion.div>

            {/* Line 4: In Front of Your Eyes Each Month - emphasized promise */}
            <motion.div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl" data-id="element-1454">
              <span className="relative inline-block bg-gradient-to-r from-amber-400 via-amber-200 to-stone-200 bg-clip-text text-transparent font-bold" style={{
              borderBottom: '3px solid rgba(251, 191, 36, 0.9)',
              paddingBottom: '4px'
            }} data-id="element-1455">
                In Front of Your Eyes Each Month
              </span>
            </motion.div>
          </motion.h1>

          {/* Peptide Salvation credential line */}
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.4
        }} className="mt-6 sm:mt-8 text-base sm:text-lg md:text-xl text-stone-400 tracking-wide max-w-2xl mx-auto" style={{
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }} data-id="element-1456">
            From the Authors of the world's biggest and most scientifically
            backed reference manual for peptides,{' '}
            <span className="text-amber-400 italic font-semibold" data-id="element-1457">
              Peptide Salvation
            </span>
          </motion.p>

          {/* PRO Identity Section — secret society invitation */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.45
        }} className="mt-8 sm:mt-10 max-w-4xl mx-auto relative" data-id="element-1458">
            {/* Pulsing border glow */}
            <motion.div className="absolute -inset-[2px] rounded-xl opacity-60" animate={{
            boxShadow: ['0 0 20px rgba(147, 51, 234, 0.4), inset 0 0 20px rgba(147, 51, 234, 0.1)', '0 0 30px rgba(217, 119, 6, 0.4), inset 0 0 20px rgba(217, 119, 6, 0.1)', '0 0 20px rgba(147, 51, 234, 0.4), inset 0 0 20px rgba(147, 51, 234, 0.1)'],
            borderColor: ['rgba(147, 51, 234, 0.5)', 'rgba(217, 119, 6, 0.5)', 'rgba(147, 51, 234, 0.5)']
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} style={{
            border: '2px solid rgba(147, 51, 234, 0.5)',
            borderRadius: '0.75rem'
          }} data-id="element-1459" />

            <div className="relative p-6 sm:p-8 md:p-10 bg-gradient-to-br from-purple-950/40 via-black/80 to-stone-950/40 rounded-xl" data-id="element-1460">
              {/* RESTRICTED ACCESS badge */}
              <div className="flex justify-center mb-6" data-id="element-1461">
                <motion.div animate={{
                opacity: [0.7, 1, 0.7]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="px-5 py-1.5 border border-amber-500/50 bg-amber-950/20 rounded-sm" data-id="element-1462">
                  <span className="text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase text-amber-400" style={{
                  letterSpacing: '0.25em'
                }} data-id="element-1463">
                    ▪ ENROLLMENT IS NOW OPEN ▪
                  </span>
                </motion.div>
              </div>

              {/* Title: PSI's PRIVATE RESEARCH ORDER */}
              <div className="text-center mb-6" data-id="element-1464">
                <p className="text-sm sm:text-base md:text-lg text-stone-400 mb-3 tracking-wide" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-1465">
                  This is the Peptide Science Institute's
                </p>
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-wider text-stone-100 mb-2" style={{
                fontFamily: 'Cinzel, serif',
                textShadow: '0 0 30px rgba(147, 51, 234, 0.3)',
                letterSpacing: '0.12em'
              }} data-id="element-1466">
                  PRIVATE RESEARCH ORDER
                </h3>
                <motion.p className="text-3xl sm:text-4xl md:text-5xl font-extrabold" animate={{
                textShadow: ['0 0 20px rgba(147, 51, 234, 0.5), 0 0 60px rgba(147, 51, 234, 0.2)', '0 0 30px rgba(217, 119, 6, 0.5), 0 0 80px rgba(217, 119, 6, 0.2)', '0 0 20px rgba(147, 51, 234, 0.5), 0 0 60px rgba(147, 51, 234, 0.2)'],
                color: ['rgb(192, 132, 252)', 'rgb(251, 191, 36)', 'rgb(192, 132, 252)']
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} style={{
                fontFamily: 'Cinzel, serif',
                letterSpacing: '0.2em'
              }} data-id="element-1467">
                  (PRO)
                </motion.p>
              </div>

              {/* Single powerful sentence */}
              <p className="text-sm sm:text-base md:text-lg text-stone-300 leading-relaxed text-center max-w-3xl mx-auto mb-8" data-id="element-1468">
                An underground collective of researchers, physicians, and elite
                biohackers exploring what conventional science{' '}
                <span className="text-stone-100 font-semibold italic" data-id="element-1469">
                  refuses to touch.
                </span>
              </p>

              {/* Three punchy lines with dividers */}
              <div className="max-w-2xl mx-auto space-y-0" data-id="element-1470">
                <div className="py-3 border-t border-purple-500/20" data-id="element-1471">
                  <p className="text-sm sm:text-base text-purple-300 font-medium text-center tracking-wide" data-id="element-1472">
                    Protocols the mainstream doesn't know exist.
                  </p>
                </div>
                <div className="py-3 border-t border-amber-500/20" data-id="element-1473">
                  <p className="text-sm sm:text-base text-amber-400/90 font-medium text-center tracking-wide" data-id="element-1474">
                    Research that rewrites what you thought was possible.
                  </p>
                </div>
                <div className="py-3 border-t border-b border-purple-500/20" data-id="element-1475">
                  <p className="text-sm sm:text-base text-stone-300 font-medium text-center tracking-wide" data-id="element-1476">
                    These are the kinds of biological secrets that even many of
                    the world's{' '}
                    <span className="text-amber-400 font-semibold" data-id="element-1477">
                      wealthiest people
                    </span>{' '}
                    have not found.
                  </p>
                </div>
              </div>

              {/* Closing line */}
              <p className="mt-6 text-sm sm:text-base text-stone-500 italic text-center" data-id="element-1478">
                This is where the future of human biology is being written —{' '}
                <span className="text-stone-300 font-semibold not-italic" data-id="element-1479">
                  in private.
                </span>
              </p>

              {/* Enrollment line */}
              <motion.p animate={{
              opacity: [0.6, 1, 0.6]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="mt-5 text-sm sm:text-base font-bold tracking-wider uppercase text-center text-amber-400" style={{
              letterSpacing: '0.15em'
            }} data-id="element-1480">
                For a limited time, we are opening enrollment
              </motion.p>
            </div>
          </motion.div>

          {/* What you get — showcasing section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.5
        }} className="mt-8 sm:mt-10 max-w-4xl mx-auto" data-id="element-1481">
            <ul className="text-left inline-block space-y-3 text-base sm:text-lg md:text-xl text-stone-300" data-id="element-1482">
              <li className="flex items-start gap-3" data-id="element-1483">
                <span className="text-amber-500 mt-1 flex-shrink-0" data-id="element-1484">→</span>
                <span data-id="element-1485">
                  Where we showcase the{' '}
                  <span className="text-amber-400 font-semibold" data-id="element-1486">
                    latest scientific research
                  </span>{' '}
                  on peptides
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1487">
                <span className="text-amber-500 mt-1 flex-shrink-0" data-id="element-1488">→</span>
                <span data-id="element-1489">
                  Demonstrate our{' '}
                  <span className="text-amber-400 font-bold" data-id="element-1490">
                    Precision Cell™ method
                  </span>{' '}
                  transformations
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1491">
                <span className="text-amber-500 mt-1 flex-shrink-0" data-id="element-1492">→</span>
                <span data-id="element-1493">
                  <span className="text-stone-100 font-semibold" data-id="element-1494">
                    Debunk the constant BS
                  </span>{' '}
                  and incorrect information being shared online
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1495">
                <span className="text-amber-500 mt-1 flex-shrink-0" data-id="element-1496">→</span>
                <span data-id="element-1497">
                  Give you access to{' '}
                  <span className="text-amber-400 font-semibold" data-id="element-1498">
                    two of the greatest biohacking minds in the world
                  </span>{' '}
                  so you can{' '}
                  <span className="text-stone-100 font-bold underline decoration-amber-500/60 underline-offset-4" data-id="element-1499">
                    ask almost any question you want
                  </span>
                </span>
              </li>
            </ul>
          </motion.div>

          {/* TESTIMONIALS — Jeff S., Michael E., Johnson E. */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 1.55
        }} className="mt-12 sm:mt-16 mb-10 sm:mb-16 max-w-4xl mx-auto" data-id="element-1500">
            {/* Section intro */}
            <div className="text-center mb-10 sm:mb-14" data-id="element-1501">
              <div className="flex items-center justify-center gap-4 mb-6" data-id="element-1502">
                <div className="w-12 sm:w-20 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-1503" />
                <div className="w-2.5 h-2.5 bg-amber-500 rotate-45" data-id="element-1504" />
                <div className="w-12 sm:w-20 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-1505" />
              </div>
              <div className="max-w-4xl mx-auto space-y-3 sm:space-y-4" data-id="element-1506">
                <p className="text-xl sm:text-2xl md:text-3xl text-stone-400 font-medium tracking-wide" style={{
                fontFamily: 'Cinzel, serif'
              }} data-id="element-1507">
                  The Transformations.
                </p>
                <p className="text-xl sm:text-2xl md:text-3xl text-stone-400 font-medium tracking-wide" style={{
                fontFamily: 'Cinzel, serif'
              }} data-id="element-1508">
                  The Insights.
                </p>
                <p className="text-xl sm:text-2xl md:text-3xl text-stone-400 font-medium tracking-wide" style={{
                fontFamily: 'Cinzel, serif'
              }} data-id="element-1509">
                  The Revelations.
                </p>

                <div className="pt-2 sm:pt-4" data-id="element-1510">
                  <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-none" style={{
                  fontFamily: 'Cinzel, serif'
                }} data-id="element-1511">
                    <span className="text-amber-400" style={{
                    textShadow: '0 0 50px rgba(217, 119, 6, 0.6), 0 0 100px rgba(217, 119, 6, 0.3)'
                  }} data-id="element-1512">
                      THEY'RE ALL REAL
                    </span>
                  </p>
                </div>

                <div className="pt-2 sm:pt-3" data-id="element-1513">
                  <p className="text-lg sm:text-xl md:text-2xl text-stone-300 font-semibold leading-relaxed" style={{
                  fontFamily: 'Cinzel, serif',
                  textShadow: '0 0 15px rgba(217, 119, 6, 0.15)'
                }} data-id="element-1514">
                    And they're happening{' '}
                    <span className="text-amber-300 font-bold italic" data-id="element-1515">
                      right now
                    </span>
                  </p>
                  <p className="text-lg sm:text-xl md:text-2xl text-stone-300 font-semibold leading-relaxed mt-1" style={{
                  fontFamily: 'Cinzel, serif',
                  textShadow: '0 0 15px rgba(147, 51, 234, 0.2)'
                }} data-id="element-1516">
                    inside the{' '}
                    <span className="text-purple-300 font-bold" style={{
                    textShadow: '0 0 25px rgba(147, 51, 234, 0.4)'
                  }} data-id="element-1517">
                      Private Research Order
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-12 sm:space-y-16" data-id="element-1518">
              {/* Testimonial — Michael E. */}
              <div className="relative" data-id="element-1519">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-1520" />
                <div className="relative px-4 sm:px-8 py-8 sm:py-10" data-id="element-1521">
                  <div className="mb-4 sm:mb-6" data-id="element-1522">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-1523">
                      "
                    </span>
                  </div>
                  <div className="relative mb-6 sm:mb-8 max-w-3xl mx-auto" data-id="element-1524">
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-1525" />
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-1526">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-1527" />
                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-1528">
                        <p data-id="element-1529">
                          I was most impressed with the{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1530">
                            sheer amount of information in each session.
                          </span>{' '}
                          The amount of time that the two spent, answering with
                          great detail was great! It was impressive that{' '}
                          <span className="text-white font-bold" data-id="element-1531">
                            even after 4 hrs, at least for me it didn't feel
                            like they were in a hurry for the sessions to end!
                          </span>
                        </p>
                        <p data-id="element-1532">
                          What made me realize this was different than other
                          gurus out there is{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1533">
                            they seem to think more outside the box
                          </span>{' '}
                          and make each recommendation{' '}
                          <span className="text-white font-bold" data-id="element-1534">
                            based on an individual's circumstances rather than a
                            cookie cutter approach.
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1535">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1536" />
                    <div className="text-center sm:text-right" data-id="element-1537">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-1538">
                        Michael E.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-1539">PSI PRO Member</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial — Jeff S. */}
              <div className="relative" data-id="element-1540">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-1541" />
                <div className="relative px-4 sm:px-8 py-8 sm:py-10" data-id="element-1542">
                  <div className="mb-4 sm:mb-6" data-id="element-1543">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-1544">
                      "
                    </span>
                  </div>
                  <div className="relative mb-6 sm:mb-8 max-w-3xl mx-auto" data-id="element-1545">
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-1546" />
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-1547">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-1548" />
                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-1549">
                        <p data-id="element-1550">
                          Hi Brenden, Dr Ali, Dan and team, First off, I wanted
                          to congratulate you for the work that you do at PSI.{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1551">
                            It is unique, inspiring and brings the tremendous
                            potential of bringing Professor Khavinson's work
                            into the sunlight
                          </span>{' '}
                          after many years out of sight. I only discovered PSI
                          about a month ago despite many years of working in
                          peptide science / regenerative medicine research work
                          and{' '}
                          <span className="text-white font-bold" data-id="element-1552">
                            it has thrown me for a loop...
                          </span>
                        </p>
                        <p data-id="element-1553">
                          Brenden, I speak a tiny bit of Russian and{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1554">
                            my hat is off to you for jumping in to translate the
                            700+ Khavinson articles
                          </span>{' '}
                          and the many other Russian journals that covered his
                          work.{' '}
                          <span className="text-white font-bold" data-id="element-1555">
                            You clearly have a brilliant mind and the ability to
                            focus
                          </span>
                          , not to mention a great tolerance for the pain you
                          inflicted upon yourself from the difficulty and volume
                          of effort this must have entailed.
                        </p>
                        <p data-id="element-1556">
                          As I'm sure you know,{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1557">
                            the US docs I work with don't talk about Khavinson's
                            work
                          </span>{' '}
                          (if they even know of it)...
                        </p>
                        <p data-id="element-1558">
                          I'm now doing my best to catch up to the recent live
                          calls with Brenden and Dr Ali (as moderated by Dan) as
                          well as go through the libraries / courses of Peptide
                          Mastery and whatever else I can find.
                        </p>
                        <p data-id="element-1559">
                          So again, I wanted to offer a note of appreciation.{' '}
                          <span className="text-white font-bold" data-id="element-1560">
                            Even the commonly known research peptides are not
                            even mentioned in the Mayo regenerative medicine
                            research we are doing
                          </span>
                          , much less the{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1561">
                            Khavinson peptides that only you gentlemen have a
                            grasp on.
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1562">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1563" />
                    <div className="text-center sm:text-right" data-id="element-1564">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-1565">
                        Jeff S.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-1566">
                        Peptide Science / Regenerative Medicine Researcher
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial — Johnson E. */}
              <div className="relative" data-id="element-1567">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-1568" />
                <div className="relative px-4 sm:px-8 py-8 sm:py-10" data-id="element-1569">
                  <div className="mb-4 sm:mb-6" data-id="element-1570">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-1571">
                      "
                    </span>
                  </div>
                  <div className="relative mb-6 sm:mb-8 max-w-3xl mx-auto" data-id="element-1572">
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-1573" />
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-1574">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-1575" />
                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-1576">
                        <p data-id="element-1577">
                          <span className="text-amber-400 font-semibold" data-id="element-1578">
                            PSI has already had a meaningful impact on how I
                            think
                          </span>{' '}
                          — and I'm only about a month in.
                        </p>
                        <p data-id="element-1579">
                          I believe I was one of the founding memberships when
                          PSI PRO opened, and I can honestly say{' '}
                          <span className="text-white font-bold" data-id="element-1580">
                            I have not regretted the decision for a single
                            second.
                          </span>
                        </p>
                        <p data-id="element-1581">
                          At this stage of my life, my motivation isn't casual
                          curiosity. I want to understand biology deeply. I want
                          to learn properly. And I want to eventually be{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1582">
                            an invaluable resource to others
                          </span>{' '}
                          who are trying to navigate their own health journey —
                          especially when it comes to powerful and emerging
                          protocols like peptides.
                        </p>
                        <p data-id="element-1583">
                          <span className="text-white font-bold" data-id="element-1584">
                            PSI feels like the place where that becomes
                            possible...
                          </span>
                        </p>
                        <p data-id="element-1585">
                          One specific example:{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1586">
                            my understanding of oral peptides completely
                            changed.
                          </span>{' '}
                          I had accepted the social media consensus about
                          bioavailability as fact. But PSI's deeper discussion
                          helped me realize{' '}
                          <span className="text-white font-bold" data-id="element-1587">
                            how incomplete and oversimplified that narrative
                            was.
                          </span>{' '}
                          That was a real "wake up" moment for me.
                        </p>
                        <p data-id="element-1588">
                          I'm also a proud owner of Peptide Mastery, Peptide
                          Salvation, and several of the additional materials.
                          But more than the content itself, what's been most
                          valuable is how{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1589">
                            PSI is training a research mindset.
                          </span>
                        </p>
                        <p className="text-stone-200 font-medium" data-id="element-1590">
                          It's teaching me how to evaluate evidence. How to
                          think clearly. How to separate signal from noise.
                        </p>
                        <p className="text-lg sm:text-xl md:text-2xl" data-id="element-1591">
                          For me, this isn't just education.
                        </p>
                        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-400" style={{
                        textShadow: '0 0 20px rgba(251, 191, 36, 0.3)'
                      }} data-id="element-1592">
                          It's clarity.
                        </p>
                        <p className="text-stone-200 font-medium" data-id="element-1593">
                          And I'm grateful to be part of it.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1594">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1595" />
                    <div className="text-center sm:text-right" data-id="element-1596">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-1597">
                        Johnson E.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-1598">
                        Founding PSI PRO Member, Peptide Mastery & Peptide
                        Salvation Owner
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial — Moussa S. */}
              <div className="relative" data-id="element-1599">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-1600" />
                <div className="relative px-4 sm:px-8 py-8 sm:py-10" data-id="element-1601">
                  <div className="mb-4 sm:mb-6" data-id="element-1602">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-1603">
                      "
                    </span>
                  </div>
                  <div className="relative mb-6 sm:mb-8 max-w-3xl mx-auto" data-id="element-1604">
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-1605" />
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-1606">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-1607" />
                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-1608">
                        <p data-id="element-1609">Hello Dan and the Whole team,</p>
                        <p data-id="element-1610">
                          A couple days ago, I completed my first reading of the
                          Peptide Mastery Complete course. All I can say is{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1611">
                            "Bravo!"
                          </span>{' '}
                          It is so interesting,{' '}
                          <span className="text-white font-bold" data-id="element-1612">
                            I could not stop myself going through it...every
                            night, some nights until 3:00 AM.
                          </span>
                        </p>
                        <p data-id="element-1613">
                          <span className="text-amber-400 font-semibold" data-id="element-1614">
                            Thank you x10!
                          </span>{' '}
                          for creating and making accessible{' '}
                          <span className="text-white font-bold" data-id="element-1615">
                            such a deep knowledge regarding human health.
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1616">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1617" />
                    <div className="text-center sm:text-right" data-id="element-1618">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-1619">
                        Moussa S.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-1620">
                        Founding PSI PRO Member & Peptide Mastery Owner
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial — Shawna C. */}
              <div className="relative" data-id="element-1621">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-1622" />
                <div className="relative px-4 sm:px-8 py-8 sm:py-10" data-id="element-1623">
                  <div className="mb-4 sm:mb-6" data-id="element-1624">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-1625">
                      "
                    </span>
                  </div>
                  <div className="relative mb-6 sm:mb-8 max-w-3xl mx-auto" data-id="element-1626">
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-1627" />
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-1628">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-1629" />
                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-1630">
                        <p data-id="element-1631">
                          At a most challenging time in my life{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1632">
                            you are all truly a gift from the Universe.
                          </span>{' '}
                          I count my blessings daily!
                        </p>
                        <p data-id="element-1633">
                          Ali, You and Brenden —{' '}
                          <span className="text-white font-bold" data-id="element-1634">
                            the best triple threat in the Healthspan/Lifespan
                            space!
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1635">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1636" />
                    <div className="text-center sm:text-right" data-id="element-1637">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-1638">
                        Shawna C.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-1639">
                        Transformation Client & Peptide Mastery Owner
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial — Max O. */}
              <div className="relative" data-id="element-1640">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-1641" />
                <div className="relative px-4 sm:px-8 py-8 sm:py-10" data-id="element-1642">
                  <div className="mb-4 sm:mb-6" data-id="element-1643">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-1644">
                      "
                    </span>
                  </div>
                  <div className="relative mb-6 sm:mb-8 max-w-3xl mx-auto" data-id="element-1645">
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-1646" />
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-1647">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-1648" />
                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-1649">
                        <p data-id="element-1650">
                          I'm writing first to express my sincere appreciation
                          for the{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1651">
                            exceptional quality of your content
                          </span>{' '}
                          which is{' '}
                          <span className="text-white font-bold" data-id="element-1652">
                            way better (and more accurate) than what can be
                            found nowadays on the internet
                          </span>{' '}
                          (and it's not even close)...
                        </p>
                        <p data-id="element-1653">
                          Please know that{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1654">
                            I greatly admire the contribution you and your team
                            are making
                          </span>{' '}
                          to the popularization and understanding of peptides.{' '}
                          <span className="text-white font-bold" data-id="element-1655">
                            Your work is truly making a difference.
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1656">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1657" />
                    <div className="text-center sm:text-right" data-id="element-1658">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-1659">
                        Max O.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-1660">
                        Peptide Mastery Owner & Peptide/Biohacking Store Owner
                        (10+ years)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* BS callout — hard-hitting line */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 1.6
        }} className="mt-8 sm:mt-10 max-w-3xl mx-auto" data-id="element-1661">
            <div className="px-6 sm:px-8 py-4 sm:py-5 border-l-4 border-r-4 border-red-500/60 bg-red-950/15 rounded-lg" data-id="element-1662">
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-stone-200 text-center leading-snug" style={{
              textShadow: '0 0 15px rgba(239, 68, 68, 0.15)'
            }} data-id="element-1663">
                The amount of BS, unproven, harmful, lazy information about
                peptides out there is{' '}
                <span className="text-red-400 font-extrabold uppercase tracking-wide" data-id="element-1664">
                  MIND BOGGLING
                </span>
              </p>
            </div>
          </motion.div>

          {/* DID YOU KNOW — BPC-157 vs mystery peptide */}
          <motion.div initial={{
          opacity: 0,
          y: 25
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.7
        }} className="mt-8 sm:mt-10 max-w-4xl mx-auto" data-id="element-1665">
            <div className="p-6 sm:p-8 md:p-10 bg-gradient-to-br from-stone-950/60 via-black/80 to-stone-950/40 border border-amber-900/30 rounded-xl" data-id="element-1666">
              {/* Header */}
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 text-center mb-6 sm:mb-8" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 25px rgba(217, 119, 6, 0.3)'
            }} data-id="element-1667">
                DID YOU KNOW...
              </p>

              <div className="space-y-5 sm:space-y-6 text-sm sm:text-base md:text-lg text-stone-300 leading-relaxed max-w-3xl mx-auto" data-id="element-1668">
                {/* BPC-157 intro */}
                <p data-id="element-1669">
                  The entire biohacking world is obsessed with{' '}
                  <span className="text-stone-100 font-bold" data-id="element-1670">BPC-157</span>.
                  It's practically worshipped as the{' '}
                  <span className="text-stone-100 italic" data-id="element-1671">
                    "ultimate healing peptide."
                  </span>
                </p>

                {/* Risks */}
                <p data-id="element-1672">
                  But most people using it have no idea that BPC-157 carries a
                  known risk of{' '}
                  <span className="text-red-400 font-semibold" data-id="element-1673">anhedonia</span>{' '}
                  — a disturbing emotional blunting where you lose the ability
                  to feel pleasure or motivation. They also don't know it may{' '}
                  <span className="text-red-400 font-semibold" data-id="element-1674">
                    accelerate cancerous tumor growth
                  </span>
                  , making it a serious risk for people with underlying (and
                  sometimes hidden) cancer.
                </p>

                {/* Mystery peptide intro */}
                <p data-id="element-1675">
                  Meanwhile, there is a{' '}
                  <span className="text-amber-400 font-bold" data-id="element-1676">
                    tiny 2-amino-acid peptide
                  </span>{' '}
                  that most biohackers have never even heard of that operates on
                  a completely different level.
                </p>

                {/* Superior claim */}
                <p className="text-base sm:text-lg md:text-xl text-stone-100 font-bold text-center py-2" data-id="element-1677">
                  It's been{' '}
                  <span className="text-green-400 uppercase tracking-wide" data-id="element-1678">
                    PROVEN
                  </span>{' '}
                  to be superior to BPC-157 in almost every way:
                </p>

                {/* Bullet points */}
                <ul className="space-y-4" data-id="element-1679">
                  <li className="flex items-start gap-3" data-id="element-1680">
                    <span className="text-amber-500 mt-1 flex-shrink-0 font-bold" data-id="element-1681">
                      →
                    </span>
                    <span data-id="element-1682">
                      It can{' '}
                      <span className="text-green-400 font-semibold" data-id="element-1683">
                        enter the cell nucleus
                      </span>{' '}
                      and physically reactivate youthful genes inside your very
                      DNA — even ones that have been silenced for years.{' '}
                      <span className="text-stone-500 italic" data-id="element-1684">
                        BPC-157 has never been proven to do this.
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1685">
                    <span className="text-amber-500 mt-1 flex-shrink-0 font-bold" data-id="element-1686">
                      →
                    </span>
                    <span data-id="element-1687">
                      It simultaneously activates both{' '}
                      <span className="text-green-400 font-semibold" data-id="element-1688">
                        massive tissue regeneration (IGF-1)
                      </span>{' '}
                      AND{' '}
                      <span className="text-green-400 font-semibold" data-id="element-1689">
                        cellular longevity pathways (Sirtuins 1 & 6)
                      </span>{' '}
                      — two systems that scientists believed were{' '}
                      <span className="text-stone-100 font-bold italic" data-id="element-1690">
                        biologically impossible to trigger at the same time!
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1691">
                    <span className="text-amber-500 mt-1 flex-shrink-0 font-bold" data-id="element-1692">
                      →
                    </span>
                    <span data-id="element-1693">
                      It has been proven to{' '}
                      <span className="text-green-400 font-semibold" data-id="element-1694">
                        repair crushed limbs
                      </span>{' '}
                      containing dead, necrotic tissue, where it literally{' '}
                      <span className="text-stone-100 font-bold" data-id="element-1695">
                        regenerated 75% of surgically removed liver tissue
                      </span>
                      .
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1696">
                    <span className="text-amber-500 mt-1 flex-shrink-0 font-bold" data-id="element-1697">
                      →
                    </span>
                    <span data-id="element-1698">
                      It has{' '}
                      <span className="text-green-400 font-bold" data-id="element-1699">
                        zero reported side effects
                      </span>{' '}
                      — even at{' '}
                      <span className="text-stone-100 font-semibold" data-id="element-1700">
                        1,000 times
                      </span>{' '}
                      the normal therapeutic dose!
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1701">
                    <span className="text-amber-500 mt-1 flex-shrink-0 font-bold" data-id="element-1702">
                      →
                    </span>
                    <span data-id="element-1703">
                      And it does{' '}
                      <span className="text-green-400 font-bold uppercase" data-id="element-1704">
                        NOT
                      </span>{' '}
                      require injection.
                    </span>
                  </li>
                </ul>

                {/* Closing tie-back */}
                <div className="pt-4 mt-4 border-t border-amber-900/30 text-center" data-id="element-1705">
                  <p className="text-base sm:text-lg text-stone-400" data-id="element-1706">
                    This is just{' '}
                    <span className="text-amber-400 font-bold" data-id="element-1707">
                      ONE peptide
                    </span>{' '}
                    out of the{' '}
                    <span className="text-stone-200 font-semibold" data-id="element-1708">
                      HUNDREDS
                    </span>{' '}
                    of other peptides, bioregulators, bioaugmentors and
                    compounds that we cover inside the{' '}
                    <span className="text-purple-300 font-semibold" data-id="element-1709">
                      Private Research Order
                    </span>{' '}
                    and{' '}
                    <span className="text-green-400 font-semibold" data-id="element-1710">
                      Peptide Mastery
                    </span>
                    .
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Subheadline */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.75
        }} className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-stone-300 font-bold leading-relaxed mt-8 sm:mt-10 md:mt-12" style={{
          textShadow: '0 0 20px rgba(217, 119, 6, 0.2)'
        }} data-id="element-1711">
            We'll Show You How to Optimize Health & Performance Beyond What 99%
            of Doctors <em className="italic" data-id="element-1712">Even Know</em> Is Possible
          </motion.p>

          {/* Gift transition — Peptide Mastery included free */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.85
        }} className="mt-10 sm:mt-12 max-w-3xl mx-auto text-center" data-id="element-1713">
            {/* Divider */}
            <div className="flex items-center justify-center gap-4 mb-6" data-id="element-1714">
              <div className="w-16 h-px bg-gradient-to-r from-transparent to-amber-600/50" data-id="element-1715" />
              <span className="text-amber-500 text-lg" data-id="element-1716">✦</span>
              <div className="w-16 h-px bg-gradient-to-l from-transparent to-amber-600/50" data-id="element-1717" />
            </div>

            <p className="text-lg sm:text-xl md:text-2xl text-stone-400 leading-relaxed mb-4" data-id="element-1718">
              And as if that weren't enough...
            </p>

            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-200 leading-snug mb-4" style={{
            textShadow: '0 0 15px rgba(34, 197, 94, 0.15)'
          }} data-id="element-1719">
              Every member also receives a{' '}
              <span className="text-green-400 font-extrabold" data-id="element-1720">FREE</span>{' '}
              scholarship to the most{' '}
              <span className="text-amber-400 font-bold" data-id="element-1721">
                elite, comprehensive, in-depth peptide education
              </span>{' '}
              ever created
            </p>

            <p className="text-sm sm:text-base text-stone-500 italic" data-id="element-1722">
              Just for being a member. No extra cost. No catch.
            </p>
          </motion.div>
        </motion.div>

        {/* Sub-headline - Completely redesigned with scholarship and access messaging */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 1.8
      }} className="mb-8 sm:mb-12 relative" data-id="element-1723">
          {/* Dramatic spotlight effect - reduced */}
          <div className="absolute inset-0 bg-gradient-radial from-green-600/5 via-transparent to-transparent blur-3xl" data-id="element-1724" />

          <div className="relative max-w-5xl mx-auto" data-id="element-1725">
            {/* Scholarship announcement box */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 2.0
          }} className="p-6 sm:p-8 bg-gradient-to-br from-green-950/30 to-black/40 border-2 border-green-500/40 rounded-xl shadow-[0_0_40px_rgba(34,197,94,0.2)] mb-6" data-id="element-1726">
              <div className="text-center space-y-4 sm:space-y-5" data-id="element-1727">
                {/* Lead with the magnitude — what it IS before what it's called */}
                <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-green-300 leading-tight" style={{
                textShadow: '0 0 20px rgba(34, 197, 94, 0.3)'
              }} data-id="element-1728">
                  Including the Most Comprehensive, In-Depth Peptide Education
                  Ever Created — Yours FREE
                </p>

                {/* The impressive stats that prove the claim */}
                <div className="text-sm sm:text-base text-stone-300 leading-relaxed max-w-3xl mx-auto space-y-3" data-id="element-1729">
                  <p data-id="element-1730">
                    Over{' '}
                    <span className="text-amber-400 font-bold" data-id="element-1731">
                      200 modules
                    </span>{' '}
                    covering{' '}
                    <span className="text-amber-400 font-bold" data-id="element-1732">
                      130+ peptides
                    </span>
                    . <span className="text-amber-400 font-bold" data-id="element-1733">57</span> of
                    the most powerful synergistic protocols ever designed. Full
                    coverage of{' '}
                    <span className="text-green-300 font-semibold" data-id="element-1734">
                      EVERY single Khavinson peptide bioregulator
                    </span>{' '}
                    — including all three versions. The power of peptide
                    bioaugmentors. More scientific references (over{' '}
                    <span className="text-amber-400 font-bold" data-id="element-1735">1,193</span>)
                    than any other biohacking program on earth.
                  </p>
                </div>

                {/* NOW reveal the name + price */}
                <div className="pt-2 border-t border-green-500/20" data-id="element-1736">
                  <p className="text-base sm:text-lg text-stone-400" data-id="element-1737">
                    This is{' '}
                    <span className="text-green-300 font-bold" data-id="element-1738">
                      Peptide Mastery 2.0
                    </span>{' '}
                    — normally{' '}
                    <span className="text-red-400 line-through font-semibold" data-id="element-1739">
                      $2,500
                    </span>{' '}
                    (pay-in-full only).{' '}
                    <span className="text-green-400 font-bold" data-id="element-1740">
                      Included FREE with your membership.
                    </span>
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Plus divider */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: 2.2
          }} className="flex items-center justify-center my-6" data-id="element-1741">
              <div className="text-4xl sm:text-5xl font-bold text-amber-300" style={{
              textShadow: '0 0 15px rgba(251, 191, 36, 0.3)'
            }} data-id="element-1742">
                +
              </div>
            </motion.div>

            {/* Direct access announcement box */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 2.4
          }} className="p-6 sm:p-8 bg-gradient-to-br from-green-950/30 to-black/40 border-2 border-green-500/40 rounded-xl shadow-[0_0_40px_rgba(34,197,94,0.2)]" data-id="element-1743">
              <div className="text-center space-y-3 sm:space-y-4" data-id="element-1744">
                <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-green-300 leading-tight" style={{
                textShadow: '0 0 20px rgba(34, 197, 94, 0.3)'
              }} data-id="element-1745">
                  12 Months of Direct Access to the Masters Who Can "Do the
                  Impossible", Brenden Henry & Dr. Ali Mazloum
                </p>
                <p className="text-base sm:text-lg md:text-xl text-stone-300 font-semibold" data-id="element-1746">
                  <span className="text-red-400 line-through font-bold" data-id="element-1747">
                    Worth $18,000+
                  </span>{' '}
                  — <span className="text-green-400 font-bold" data-id="element-1748">INCLUDED</span>
                </p>
                <p className="text-sm sm:text-base text-green-300/80 italic" data-id="element-1749">
                  (It normally costs $1,500 an hour to gain private access to
                  either of them. As a member you will get continual guidance
                  throughout 2026 in the form of group sessions, access to
                  completely new dimensions of thinking about peptides,
                  pharmacology and biology AND a whole lot more)
                </p>
              </div>
            </motion.div>

            {/* Investment pricing box — THE CLIMAX */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 2.8
          }} className="mt-8 relative" data-id="element-1750">
              {/* Outer glow */}
              <motion.div className="absolute -inset-[3px] rounded-2xl opacity-50" animate={{
              boxShadow: ['0 0 30px rgba(217, 119, 6, 0.4), 0 0 60px rgba(217, 119, 6, 0.15)', '0 0 40px rgba(251, 191, 36, 0.5), 0 0 80px rgba(251, 191, 36, 0.2)', '0 0 30px rgba(217, 119, 6, 0.4), 0 0 60px rgba(217, 119, 6, 0.15)']
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} style={{
              border: '2px solid rgba(217, 119, 6, 0.5)',
              borderRadius: '1rem'
            }} data-id="element-1751" />

              <div className="relative p-6 sm:p-8 md:p-10 bg-gradient-to-br from-amber-950/40 via-black/80 to-stone-950/40 rounded-2xl" data-id="element-1752">
                {/* Value stack recap */}
                <div className="text-center mb-6 sm:mb-8" data-id="element-1753">
                  <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-stone-500 mb-4" data-id="element-1754">
                    So let's add it up
                  </p>
                  <div className="space-y-2 max-w-md mx-auto" data-id="element-1755">
                    <div className="flex justify-between items-center text-sm sm:text-base text-stone-400 border-b border-stone-800/50 pb-2" data-id="element-1756">
                      <span data-id="element-1757">Peptide Mastery 2.0 (200+ modules)</span>
                      <span className="text-red-400 line-through font-semibold" data-id="element-1758">
                        $2,500
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm sm:text-base text-stone-400 border-b border-stone-800/50 pb-2" data-id="element-1759">
                      <span data-id="element-1760">12 Months Direct Access</span>
                      <span className="text-red-400 line-through font-semibold" data-id="element-1761">
                        $18,000+
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm sm:text-base text-stone-400 border-b border-stone-800/50 pb-2" data-id="element-1762">
                      <span data-id="element-1763">Monthly LIVE Research Briefings</span>
                      <span className="text-red-400 line-through font-semibold italic" data-id="element-1764">
                        Priceless
                      </span>
                    </div>
                  </div>
                </div>

                {/* Total value */}
                <div className="text-center mb-6" data-id="element-1765">
                  <p className="text-base sm:text-lg text-stone-500 mb-1" data-id="element-1766">
                    Combined Real-World Value:
                  </p>
                  <p className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-red-400 line-through" style={{
                  textShadow: '0 0 15px rgba(239, 68, 68, 0.2)'
                }} data-id="element-1767">
                    $20,500+
                  </p>
                </div>

                {/* Dramatic divider */}
                <div className="flex items-center justify-center gap-3 my-6" data-id="element-1768">
                  <div className="w-20 h-px bg-gradient-to-r from-transparent to-amber-500/50" data-id="element-1769" />
                  <span className="text-amber-400 text-xs uppercase tracking-[0.3em] font-bold" data-id="element-1770">
                    Your Price
                  </span>
                  <div className="w-20 h-px bg-gradient-to-l from-transparent to-amber-500/50" data-id="element-1771" />
                </div>

                {/* THE PRICE — the wow moment */}
                <div className="text-center space-y-4" data-id="element-1772">
                  <motion.p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-amber-300" animate={{
                  textShadow: ['0 0 20px rgba(251, 191, 36, 0.3)', '0 0 40px rgba(251, 191, 36, 0.5)', '0 0 20px rgba(251, 191, 36, 0.3)']
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} style={{
                  fontFamily: 'Cinzel, serif'
                }} data-id="element-1773">
                    Only $249 Today
                  </motion.p>
                  <p className="text-base sm:text-lg text-stone-400 -mt-1" data-id="element-1774">
                    Then 11 monthly payments of $249
                  </p>

                  {/* Annual option */}
                  <div className="pt-4 mt-2 border-t border-amber-900/30" data-id="element-1775">
                    <p className="text-lg sm:text-xl text-stone-300" data-id="element-1776">
                      Or pay{' '}
                      <span className="text-amber-400 font-bold text-xl sm:text-2xl" data-id="element-1777">
                        $2,490
                      </span>{' '}
                      in full today{' '}
                      <span className="text-green-400 font-semibold" data-id="element-1778">
                        (save $498)
                      </span>
                    </p>
                    <p className="text-sm sm:text-base text-green-400/90 font-medium mt-2" data-id="element-1779">
                      ✦ Annual members instantly unlock{' '}
                      <span className="font-bold text-green-300" data-id="element-1780">
                        ALL 200+ modules
                      </span>{' '}
                      of Peptide Mastery immediately
                    </p>
                  </div>

                  {/* Lock-in guarantee */}
                  <p className="text-xs sm:text-sm text-stone-500 italic pt-2" data-id="element-1781">
                    Lock in this rate for life — renew at the same price as long
                    as you stay
                  </p>
                </div>
              </div>
            </motion.div>

            {/* CTA Button to scroll to pricing */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 2.8
          }} className="mt-8 flex justify-center" data-id="element-1782">
              <button onClick={() => {
              const pricingSection = document.querySelector('[data-pricing-section]');
              if (pricingSection) {
                pricingSection.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }
            }} className="px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black text-lg sm:text-xl font-bold rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 min-h-[44px]" data-id="element-1783">
                Join the Private Research Order Now
              </button>
            </motion.div>
          </div>
        </motion.div>

        {/* DNA Helix Divider - Scientific but mystical */}
        <motion.div initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 2.2
      }} className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-1784">
          <motion.div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-600 to-amber-600" animate={{
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1785" />
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-1786">
            <svg width="24" height="24" viewBox="0 0 24 24" className="text-amber-500" data-id="element-1787">
              <circle cx="12" cy="6" r="2" fill="currentColor" opacity="0.8" data-id="element-1788" />
              <circle cx="12" cy="12" r="2" fill="currentColor" data-id="element-1789" />
              <circle cx="12" cy="18" r="2" fill="currentColor" opacity="0.8" data-id="element-1790" />
              <path d="M12 6 L12 18" stroke="currentColor" strokeWidth="1" opacity="0.4" data-id="element-1791" />
            </svg>
          </motion.div>
          <motion.div className="w-32 h-px bg-gradient-to-l from-transparent via-amber-600 to-amber-600" animate={{
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1792" />
        </motion.div>

        {/* Target audience - Enhanced with better breaks and emphasis */}
        <motion.p initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 2.6
      }} className="text-lg sm:text-xl md:text-2xl text-stone-300 mb-10 sm:mb-16 leading-relaxed max-w-5xl mx-auto" data-id="element-1793">
          If you're a doctor, clinician, coach, practitioner,
          <br data-id="element-1794" />
          or an advanced biohacker ready to gain{' '}
          <span className="font-bold text-amber-400 px-2 py-1 border border-amber-500/30 bg-amber-950/30 rounded" style={{
          textShadow: '0 0 20px rgba(251, 191, 36, 0.4)'
        }} data-id="element-1795">
            REAL control
          </span>{' '}
          of human biology
          <br data-id="element-1796" />
          (for yourself or for others)...
        </motion.p>

        {/* DRAMATIC PAUSE - then the hook (NO BOX, NO BREATHING) */}
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.2,
        delay: 3.5,
        type: 'spring',
        stiffness: 80
      }} className="mb-8 sm:mb-12 relative" data-id="element-1797">
          {/* Dramatic spotlight effect behind text */}
          <motion.div className="absolute inset-0 -inset-x-20 bg-gradient-radial from-amber-600/20 via-amber-600/5 to-transparent blur-3xl" animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1798" />

          {/* Subtle particle effects around the text */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1799">
            {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute w-1 h-1 bg-amber-400 rounded-full" style={{
            left: `${20 + i * 15}%`,
            top: '50%'
          }} animate={{
            y: [-20, -40, -20],
            opacity: [0, 1, 0]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            delay: i * 0.5,
            ease: 'easeInOut'
          }} data-id="element-1800" />)}
          </div>

          <div className="relative text-center max-w-4xl mx-auto space-y-2 sm:space-y-4" data-id="element-1801">
            <motion.p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold" style={{
            background: 'linear-gradient(to right, #fbbf24, #f59e0b, #fbbf24)',
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 0 40px rgba(251, 191, 36, 0.4))'
          }} animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-1802">
              This could be the
            </motion.p>

            <motion.p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-amber-400" style={{
            textShadow: '0 0 60px rgba(251, 191, 36, 0.6)'
          }} data-id="element-1803">
              most life-changing opportunity
              <br data-id="element-1804" />
              you ever get
            </motion.p>
          </div>

          {/* Subtle underline accent */}
          <motion.div className="mx-auto mt-6 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent" style={{
          width: '60%'
        }} initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          duration: 1,
          delay: 4,
          ease: 'easeOut'
        }} data-id="element-1805" />

          {/* Important note */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 4.2
        }} className="text-base sm:text-lg md:text-xl text-white mt-8 max-w-3xl mx-auto leading-relaxed" data-id="element-1806">
            PLEASE NOTE: The Private Research Order is not for "normal" people.
            This is for those ready to access the deepest secrets of health,
            performance, and regeneration beyond anything else you've seen.
          </motion.p>
        </motion.div>

        {/* TESTIMONIALS SECTION - Supporting Claims */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 4.2
      }} className="mt-12 sm:mt-20 mb-10 sm:mb-16 max-w-4xl mx-auto" data-id="element-1807">
          {/* Section intro - Purple accent */}
          <div className="text-center mb-10 sm:mb-16" data-id="element-1808">
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 4.4
          }} className="text-purple-400 text-lg sm:text-xl font-semibold mb-3" style={{
            textShadow: '0 0 20px rgba(192, 132, 252, 0.3)'
          }} data-id="element-1809">
              Real Results From Real Clients & Customers
            </motion.p>
            <motion.div className="w-32 h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent mx-auto" initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            duration: 0.8,
            delay: 4.6
          }} data-id="element-1810" />
          </div>

          {/* Testimonials - Single Column, Stacked */}
          <div className="space-y-10 sm:space-y-16" data-id="element-1811">
            {/* Testimonial 1 - Sean L. */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 4.7
          }} className="relative" data-id="element-1812">
              {/* Subtle background glow - purple tint */}
              <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-1813" />

              <div className="relative px-4 sm:px-8 py-8 sm:py-10" data-id="element-1814">
                {/* Profile Image */}
                <div className="flex justify-center mb-6" data-id="element-1815">
                  <div className="relative" data-id="element-1816">
                    <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-1817" />
                    <img src="https://cdn.magicpatterns.com/uploads/8TaJVJxgg4BMR3q5DqSNsr/SeanL.jpg" alt="Sean L." className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-purple-500/40 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-1818" />
                  </div>
                </div>

                {/* Large opening quote - purple */}
                <div className="mb-4 sm:mb-6" data-id="element-1819">
                  <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-1820">
                    "
                  </span>
                </div>

                {/* TV Screen Frame around testimonial text */}
                <div className="relative mb-6 sm:mb-8 max-w-3xl" data-id="element-1821">
                  {/* Outer purple glow */}
                  <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-1822" />

                  {/* TV screen frame */}
                  <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-1823">
                    {/* Screen scanline effect (subtle) */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-1824" />

                    {/* Testimonial text - with emphasis on key phrases */}
                    <p className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10" data-id="element-1825">
                      They're doing something{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-1826">
                        revolutionary
                      </span>{' '}
                      here. You'll be getting something extremely personalized,
                      you'll be getting the care that you need, you'll be
                      getting the support you need, the multi-faceted support
                      you need, should you decide to work with the Peptide
                      Science Institute, both on the emotional front, mental
                      front, and on the medical front. Working with them is just
                      a fantastic experience and I highly recommend this to
                      anybody who's even considering, if you're looking for
                      something{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-1827">
                        life-changing
                      </span>
                      , if you want to{' '}
                      <span className="text-white font-bold" data-id="element-1828">
                        attack diseases that the mainstream says is not really
                        something treatable
                      </span>
                      ...{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-1829">
                        it will blow your mind.
                      </span>{' '}
                      That's what I can guarantee.
                    </p>
                  </div>
                </div>

                {/* Attribution with client type */}
                <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1830">
                  <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1831" />
                  <div className="text-center sm:text-right" data-id="element-1832">
                    <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-1833">
                      Sean L.
                    </p>
                    <p className="text-stone-500 text-sm" data-id="element-1834">
                      Transformation Services Client
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Testimonial 2 - Wilfred A. */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 4.9
          }} className="relative" data-id="element-1835">
              {/* Subtle background glow - purple tint */}
              <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-1836" />

              <div className="relative px-4 sm:px-8 py-8 sm:py-10" data-id="element-1837">
                {/* Profile Image */}
                <div className="flex justify-center mb-6" data-id="element-1838">
                  <div className="relative" data-id="element-1839">
                    <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-1840" />
                    <img src="https://cdn.magicpatterns.com/uploads/3sr1cDaEEULSv8K4Ew3yfQ/WilfredA.jpg" alt="Wilfred A." className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-purple-500/40 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-1841" />
                  </div>
                </div>

                {/* Large opening quote - purple */}
                <div className="mb-4 sm:mb-6" data-id="element-1842">
                  <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-1843">
                    "
                  </span>
                </div>

                {/* TV Screen Frame around testimonial text */}
                <div className="relative mb-6 sm:mb-8 max-w-3xl" data-id="element-1844">
                  {/* Outer purple glow */}
                  <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-1845" />

                  {/* TV screen frame */}
                  <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-1846">
                    {/* Screen scanline effect (subtle) */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-1847" />

                    {/* Testimonial text - with emphasis on key phrases */}
                    <p className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10" data-id="element-1848">
                      I{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-1849">
                        100% would put my name behind
                      </span>{' '}
                      Ali & Brenden. I've had other health coaches in the past
                      (most are MD's), and I must say, Brenden & Ali are{' '}
                      <span className="text-white font-bold" data-id="element-1850">
                        FAR more in depth, systems-oriented, and rigorous
                      </span>{' '}
                      than anything I've ever seen. Also I did grab that
                      peptides course. It has given me the knowledge to turn
                      familys' health conditions around even when the medical
                      system has failed them. I owe this to the lessons I've
                      acquired from Ali & Brenden. In terms of level of positive
                      impact on my life, they are likely in the{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-1851">
                        top 5 most important people I've ever met
                      </span>{' '}
                      as they have{' '}
                      <span className="text-white font-bold" data-id="element-1852">
                        added many years to my life
                      </span>{' '}
                      and to those of my loved ones.
                    </p>
                  </div>
                </div>

                {/* Attribution with client type */}
                <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1853">
                  <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1854" />
                  <div className="text-center sm:text-right" data-id="element-1855">
                    <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-1856">
                      Wilfred A.
                    </p>
                    <p className="text-stone-500 text-sm" data-id="element-1857">
                      Peptide Mastery Owner & Transformation Services Client
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Testimonial 3 - Ronald R. */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 5.1
          }} className="relative" data-id="element-1858">
              {/* Subtle background glow - purple tint */}
              <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-1859" />

              <div className="relative px-4 sm:px-8 py-8 sm:py-10" data-id="element-1860">
                {/* Large opening quote - purple */}
                <div className="mb-4 sm:mb-6" data-id="element-1861">
                  <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-1862">
                    "
                  </span>
                </div>

                {/* TV Screen Frame around testimonial text */}
                <div className="relative mb-6 sm:mb-8 max-w-3xl" data-id="element-1863">
                  {/* Outer purple glow */}
                  <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-1864" />

                  {/* TV screen frame */}
                  <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-1865">
                    {/* Screen scanline effect (subtle) */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-1866" />

                    {/* Testimonial text - with emphasis on key phrases */}
                    <p className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10" data-id="element-1867">
                      I am a{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-1868">
                        physician with an integrative medicine practice
                      </span>{' '}
                      and your work has been{' '}
                      <span className="text-white font-bold" data-id="element-1869">
                        very valuable in treating my patients.
                      </span>{' '}
                      When I lecture to physicians I always mention "peptide
                      mastery" as a great source of material and{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-1870">
                        extensive references
                      </span>
                      ... It has been very helpful in my care of patients and I
                      appreciate the extensive references.
                    </p>
                  </div>
                </div>

                {/* Attribution with client type */}
                <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1871">
                  <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1872" />
                  <div className="text-center sm:text-right" data-id="element-1873">
                    <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-1874">
                      Ronald R.
                    </p>
                    <p className="text-stone-500 text-sm" data-id="element-1875">
                      Peptide Mastery Owner
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* MASSIVE BREATHING ROOM - Let the claim sink in */}

        {/* For The Ones Who Already Know - COMPLETELY REDESIGNED OFFER BOX */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 4.3
      }} className="mb-8 sm:mb-12 relative" data-id="element-1876">
          {/* Distinctive outer glow to separate from page */}
          <div className="absolute -inset-4 bg-gradient-to-r from-amber-600/20 via-purple-600/20 to-cyan-600/20 blur-2xl rounded-lg" data-id="element-1877" />

          {/* Main offer box with SIMPLE BLACK background */}
          <div className="relative p-6 sm:p-10 md:p-12 bg-black border-4 border-amber-500/60 rounded-lg shadow-[0_0_80px_rgba(217,119,6,0.4)]" data-id="element-1878">
            {/* Decorative top accent */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-1879"></div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 4.3
          }} className="text-center mb-6 sm:mb-8" data-id="element-1880">
              <div className="inline-block px-4 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-amber-600/20 via-amber-500/30 to-amber-600/20 border-2 border-amber-500/60 rounded-lg" data-id="element-1881">
                <p className="text-amber-300 text-lg sm:text-xl md:text-2xl font-bold tracking-wide" style={{
                textShadow: '0 0 20px rgba(252, 211, 77, 0.5)'
              }} data-id="element-1882">
                  SPECIAL PRO MEMBER OFFER
                </p>
              </div>
            </motion.div>

            {/* Celebratory Trophy Animation - Moved below offer box */}

            {/* Updated headline */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 4.5
          }} className="mt-4 sm:mt-6 mb-8 sm:mb-10" data-id="element-1883"></motion.div>

            {/* Divider */}
            <div className="flex items-center justify-center gap-4 my-8 sm:my-10" data-id="element-1884">
              <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-1885" />
              <div className="w-3 h-3 bg-amber-500 rotate-45" data-id="element-1886" />
              <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-1887" />
            </div>

            {/* WHO THIS IS FOR Section */}
            <div className="mb-8 sm:mb-10" data-id="element-1888">
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 5.2
            }} className="text-center mb-6 sm:mb-8" data-id="element-1889">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-4" style={{
                textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
              }} data-id="element-1890">
                  👥 WHO THIS IS FOR
                </h3>
                <div className="w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto" data-id="element-1891" />
              </motion.div>

              <div className="space-y-6 max-w-3xl mx-auto text-stone-300 mb-8 sm:mb-10" data-id="element-1892">
                <p className="text-base sm:text-lg text-center" data-id="element-1893">
                  Three groups:
                </p>

                <div className="space-y-4 sm:space-y-6" data-id="element-1894">
                  <div className="p-4 sm:p-6 bg-cyan-950/20 border-l-4 border-cyan-500 rounded-r-lg" data-id="element-1895">
                    <p className="font-bold text-lg sm:text-xl text-cyan-300 mb-2 sm:mb-3" data-id="element-1896">
                      1) Intelligent Biohackers
                    </p>
                    <p className="text-stone-300 text-sm sm:text-base" data-id="element-1897">
                      For those who want total mastery over their own biology
                      (and the ability to help others with their health &
                      performance). Peptide Mastery 2.0, the monthly content and
                      the Q&A sessions can help you gain biological superpowers.
                    </p>
                  </div>

                  <div className="p-4 sm:p-6 bg-emerald-950/20 border-l-4 border-emerald-500 rounded-r-lg" data-id="element-1898">
                    <p className="font-bold text-lg sm:text-xl text-emerald-300 mb-2 sm:mb-3" data-id="element-1899">
                      2) Clinicians/Coaches/Practitioners
                    </p>
                    <p className="text-stone-300 text-sm sm:text-base" data-id="element-1900">
                      For the professionals who want to be the "go-to" expert
                      and the ability to help almost ANYONE, even if everyone
                      else has failed. Peptide Mastery 2.0, the Q&A sessions,
                      Brenden's intel drops and particularly Dr. Ali's
                      diagnostics & medical work will take you to elite levels.
                    </p>
                  </div>

                  <div className="p-4 sm:p-6 bg-purple-950/20 border-l-4 border-purple-500 rounded-r-lg" data-id="element-1901">
                    <p className="font-bold text-lg sm:text-xl text-purple-300 mb-2 sm:mb-3" data-id="element-1902">
                      3) Smart People Who Want Access to the Best
                    </p>
                    <p className="text-stone-300 text-sm sm:text-base" data-id="element-1903">
                      For those who simply want shortcuts to the best answers,
                      protocols, guidance and results whenever needed. Peptide
                      Mastery 2.0 with its masterful Dosing & Reference Sheet,
                      the 54 synergistic protocols, the modules covering every
                      part of the body, all the monthly content and the Q&A
                      sessions will allow you to find EXACTLY how get the best
                      results without needing to constantly study.
                    </p>
                  </div>
                </div>

                <div className="text-center space-y-3 sm:space-y-4 py-4 sm:py-6" data-id="element-1904">
                  <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-1905">
                    We're looking for leaders who can lead themselves AND lead
                    others.
                  </p>
                  <p className="text-base sm:text-lg text-stone-300" data-id="element-1906">
                    People who are proactive, committed and take FULL
                    responsibility.
                  </p>
                  <p className="text-base sm:text-lg text-stone-300" data-id="element-1907">
                    People who want to execute with certainty in a world full of
                    noise.
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-amber-400 mt-4 sm:mt-6" style={{
                  textShadow: '0 0 20px rgba(251, 191, 36, 0.4)'
                }} data-id="element-1908">
                    This is the opportunity you've been waiting for.
                  </p>
                </div>
              </div>
            </div>

            {/* Divider - Added below "This is the opportunity you've been waiting for." */}
            <div className="flex items-center justify-center gap-4 my-8 sm:my-10" data-id="element-1909">
              <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-1910" />
              <div className="w-3 h-3 bg-amber-500 rotate-45" data-id="element-1911" />
              <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-1912" />
            </div>

            {/* WHAT THIS IS Section */}
            <div className="mb-8 sm:mb-10" data-id="element-1913">
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 5.15
            }} className="text-center mb-6 sm:mb-8" data-id="element-1914">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-4 flex items-center justify-center gap-3" style={{
                textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
              }} data-id="element-1915">
                  <span className="text-3xl sm:text-4xl" data-id="element-1916">🧬</span> WHAT THIS IS
                </h3>
                <div className="w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto" data-id="element-1917" />
              </motion.div>

              <div className="space-y-6 max-w-3xl mx-auto text-stone-300" data-id="element-1918">
                <p className="text-base sm:text-lg text-center" data-id="element-1919">
                  Right now, new for 2026: The Peptide Science Institute is
                  opening an{' '}
                  <span className="text-purple-400 font-bold" data-id="element-1920">
                    elite, exclusive underground intelligence initiative.
                  </span>
                </p>

                <div className="p-4 sm:p-6 bg-purple-950/20 border-l-4 border-purple-500 rounded-r-lg" data-id="element-1921">
                  <p className="text-lg sm:text-xl font-bold text-purple-300 mb-2 sm:mb-3 text-center" data-id="element-1922">
                    It's called the Private Research Order (PRO).
                  </p>
                  <p className="text-stone-300 text-center text-sm sm:text-base" data-id="element-1923">
                    And we're looking for the best people to join us as founding
                    members.
                  </p>
                </div>

                <div className="text-center space-y-3 sm:space-y-4 py-4 sm:py-6" data-id="element-1924">
                  <p className="text-lg sm:text-xl" data-id="element-1925">
                    <span className="text-amber-400 font-bold" data-id="element-1926">
                      Our mission:
                    </span>{' '}
                    To empower members with the awe-inspiring ability to{' '}
                    <span className="font-bold text-white" data-id="element-1927">
                      control human biology at the cellular level.
                    </span>
                  </p>
                  <p className="text-base sm:text-lg italic text-stone-400" data-id="element-1928">
                    Whether you want to heal, enhance performance, or optimize
                    at the highest levels.
                  </p>
                </div>

                <p className="text-xl sm:text-2xl font-bold text-white text-center" data-id="element-1929">
                  This is a complete intelligence immersion.
                </p>

                <p className="text-base sm:text-lg text-center" data-id="element-1930">
                  You get direct, ongoing access to the two masterminds who
                  decoded the peptide and biological matrix:
                </p>

                {/* Expert Cards - Side by Side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6 sm:mt-8" data-id="element-1931">
                  {/* Brenden Henry Card */}
                  <motion.div initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.5,
                  delay: 5.3
                }} className="p-4 sm:p-6 bg-gradient-to-br from-cyan-950/20 to-black border-2 border-cyan-500/60 rounded-lg" data-id="element-1932">
                    <div className="flex justify-center mb-4" data-id="element-1933">
                      <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center" data-id="element-1934">
                        <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20" data-id="element-1935">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-1936" />
                        </svg>
                      </div>
                    </div>
                    <h4 className="text-2xl font-bold text-cyan-300 mb-2 text-center" data-id="element-1937">
                      Brenden Henry
                    </h4>
                    <p className="text-cyan-200 font-semibold text-center mb-4" data-id="element-1938">
                      WORLD'S #1 PEPTIDE AUTHORITY
                    </p>
                    <ul className="space-y-2 text-sm text-stone-300" data-id="element-1939">
                      <li className="flex items-start gap-2" data-id="element-1940">
                        <span className="text-cyan-400 mt-1" data-id="element-1941">•</span>
                        <span data-id="element-1942">
                          <span className="font-bold text-cyan-300" data-id="element-1943">
                            First & ONLY person
                          </span>{' '}
                          to translate every original Russian peptide experiment
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-1944">
                        <span className="text-cyan-400 mt-1" data-id="element-1945">•</span>
                        <span data-id="element-1946">
                          Creator of{' '}
                          <span className="font-semibold text-cyan-300" data-id="element-1947">
                            Peptide Mastery 2.0
                          </span>{' '}
                          - the most advanced peptide education in history
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-1948">
                        <span className="text-cyan-400 mt-1" data-id="element-1949">•</span>
                        <span data-id="element-1950">
                          Primary author of{' '}
                          <span className="italic text-cyan-300" data-id="element-1951">
                            Peptide Salvation
                          </span>{' '}
                          - the world's biggest book on peptides
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-1952">
                        <span className="text-cyan-400 mt-1" data-id="element-1953">•</span>
                        <span data-id="element-1954">
                          Originator of the{' '}
                          <span className="font-semibold text-cyan-300" data-id="element-1955">
                            Henry Cofactor Principle
                          </span>
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-1956">
                        <span className="text-cyan-400 mt-1" data-id="element-1957">•</span>
                        <span data-id="element-1958">
                          Identified the class of "super peptides" known as{' '}
                          <span className="font-semibold text-cyan-300" data-id="element-1959">
                            bioaugmentors
                          </span>
                        </span>
                      </li>
                    </ul>
                    <p className="text-cyan-300 font-bold text-center mt-4" data-id="element-1960">
                      No one else comes close.
                    </p>
                  </motion.div>

                  {/* Dr. Ali Mazloum Card */}
                  <motion.div initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.5,
                  delay: 5.4
                }} className="p-4 sm:p-6 bg-gradient-to-br from-emerald-950/20 to-black border-2 border-emerald-500/60 rounded-lg" data-id="element-1961">
                    <div className="flex justify-center mb-4" data-id="element-1962">
                      <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center" data-id="element-1963">
                        <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20" data-id="element-1964">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-1965" />
                        </svg>
                      </div>
                    </div>
                    <h4 className="text-2xl font-bold text-emerald-300 mb-2 text-center" data-id="element-1966">
                      Dr. Ali Mazloum
                    </h4>
                    <p className="text-emerald-200 font-semibold text-center mb-4" data-id="element-1967">
                      "THE HUMAN MRI"
                    </p>
                    <ul className="space-y-2 text-sm text-stone-300" data-id="element-1968">
                      <li className="flex items-start gap-2" data-id="element-1969">
                        <span className="text-emerald-400 mt-1" data-id="element-1970">•</span>
                        <span data-id="element-1971">
                          <span className="font-bold text-emerald-300" data-id="element-1972">
                            Diagnostic genius
                          </span>{' '}
                          who spots what entire medical teams miss
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-1973">
                        <span className="text-emerald-400 mt-1" data-id="element-1974">•</span>
                        <span data-id="element-1975">
                          Creates{' '}
                          <span className="font-semibold text-emerald-300" data-id="element-1976">
                            "impossible" transformations
                          </span>{' '}
                          that make specialists ask "how?"
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-1977">
                        <span className="text-emerald-400 mt-1" data-id="element-1978">•</span>
                        <span data-id="element-1979">
                          Often idolized by other medical specialists at his
                          hospital
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-1980">
                        <span className="text-emerald-400 mt-1" data-id="element-1981">•</span>
                        <span data-id="element-1982">
                          Head Transformation Specialist at the{' '}
                          <span className="font-semibold text-emerald-300" data-id="element-1983">
                            Peptide Science Institute
                          </span>
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-1984">
                        <span className="text-emerald-400 mt-1" data-id="element-1985">•</span>
                        <span data-id="element-1986">
                          Complete{' '}
                          <span className="font-semibold text-emerald-300" data-id="element-1987">
                            "full stack"
                          </span>{' '}
                          master of the human body
                        </span>
                      </li>
                    </ul>
                    <p className="text-emerald-300 font-bold text-center mt-4" data-id="element-1988">
                      Any opportunity to learn from him is one you should take.
                    </p>
                  </motion.div>
                </div>

                <div className="text-center space-y-3 sm:space-y-4 py-4 sm:py-6 mt-6 sm:mt-8" data-id="element-1989">
                  <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-1990">
                    So, at this point you should realize...
                  </p>
                  <p className="text-base sm:text-lg text-stone-300" data-id="element-1991">
                    This isn't some random online course with videos for you to
                    forget about.
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-amber-400" style={{
                  textShadow: '0 0 20px rgba(251, 191, 36, 0.4)'
                }} data-id="element-1992">
                    This is an invitation to completely upgrade your life.
                  </p>
                  <p className="text-base sm:text-lg text-stone-300" data-id="element-1993">
                    With an elite, continual intelligence feed throughout 2026.
                  </p>
                  <p className="text-lg sm:text-xl font-bold text-white" data-id="element-1994">
                    A living, breathing command center.
                  </p>
                </div>
              </div>
            </div>

            {/* Divider before Where you get */}
            <div className="flex items-center justify-center gap-4 my-8 sm:my-10" data-id="element-1995">
              <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-1996" />
              <div className="w-3 h-3 bg-amber-500 rotate-45" data-id="element-1997" />
              <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-1998" />
            </div>

            {/* WHERE YOU GET Section - Redesigned for visual flow */}
            <div className="mb-8 sm:mb-10" data-id="element-1999">
              <div className="text-center mb-6 sm:mb-8" data-id="element-2000">
                <p className="text-xl sm:text-2xl font-bold text-amber-400 mb-4" data-id="element-2001">
                  Where you get:
                </p>
                <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto" data-id="element-2002" />
              </div>

              <div className="grid gap-4 sm:gap-6 max-w-4xl mx-auto" data-id="element-2003">
                {/* Benefit 1 */}
                <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: 5.3
              }} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-gradient-to-r from-cyan-950/20 to-transparent border-l-4 border-cyan-500 rounded-r-lg" data-id="element-2004">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-cyan-500 rounded-full flex items-center justify-center text-black font-bold text-sm sm:text-base" data-id="element-2005">
                    1
                  </div>
                  <div data-id="element-2006">
                    <p className="font-bold text-base sm:text-lg text-cyan-300 mb-1 sm:mb-2" data-id="element-2007">
                      Direct, live access to Brenden Henry & Dr. Ali Mazloum
                      each month
                    </p>
                    <p className="text-stone-400 text-xs sm:text-sm" data-id="element-2008">
                      The two people who literally wrote the book on peptides.
                      And who've reversed "irreversible" conditions more times
                      than I can count.
                    </p>
                  </div>
                </motion.div>

                {/* Benefit 2 */}
                <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: 5.4
              }} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-gradient-to-r from-purple-950/20 to-transparent border-l-4 border-purple-500 rounded-r-lg" data-id="element-2009">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-purple-500 rounded-full flex items-center justify-center text-black font-bold text-sm sm:text-base" data-id="element-2010">
                    2
                  </div>
                  <div data-id="element-2011">
                    <p className="font-bold text-base sm:text-lg text-purple-300 mb-1 sm:mb-2" data-id="element-2012">
                      The Precision Cell™ Pro methodology
                    </p>
                    <p className="text-stone-400 text-xs sm:text-sm" data-id="element-2013">
                      You'll get to learn the core transformation framework
                      Brenden, Ali and our team use to get incredible results
                      for our private clients – ones paying $15,000 to $120,000.
                    </p>
                  </div>
                </motion.div>

                {/* Benefit 3 */}
                <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: 5.5
              }} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-gradient-to-r from-amber-950/20 to-transparent border-l-4 border-amber-500 rounded-r-lg" data-id="element-2014">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold text-sm sm:text-base" data-id="element-2015">
                    3
                  </div>
                  <div data-id="element-2016">
                    <p className="font-bold text-base sm:text-lg text-amber-300 mb-1 sm:mb-2" data-id="element-2017">
                      The complete Peptide Mastery 2.0 program
                    </p>
                    <p className="text-stone-400 text-xs sm:text-sm" data-id="element-2018">
                      130+ peptides. 54 of the most advanced multi-peptide
                      protocols ever created. 1,193 scientific citations. The
                      highest level and most complete peptide education in the
                      world. This alone deserves an hour-long video explaining
                      how powerful the information is. It is genuinely a
                      masterwork. It normally costs $2,500 on its own.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* THREE-PILLAR DELIVERY MODEL */}
            <div className="mb-8 sm:mb-10 mt-10 sm:mt-14" data-id="element-2019">
              {/* Section Header */}
              <div className="flex items-center justify-center gap-4 mb-6 sm:mb-8" data-id="element-2020">
                <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-2021" />
                <div className="w-3 h-3 bg-amber-500 rotate-45" data-id="element-2022" />
                <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-2023" />
              </div>
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 5.6
            }} className="text-center mb-8 sm:mb-10" data-id="element-2024">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-3" style={{
                textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
              }} data-id="element-2025">
                  🏛️ THE THREE-PILLAR DELIVERY MODEL
                </h3>
                <p className="text-base sm:text-lg text-stone-400 max-w-2xl mx-auto" data-id="element-2026">
                  How your monthly intelligence is delivered
                </p>
                <div className="w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto mt-4" data-id="element-2027" />
              </motion.div>

              {/* Three Pillar Cards */}
              <div className="grid grid-cols-1 gap-6 sm:gap-8 max-w-4xl mx-auto" data-id="element-2028">
                {/* PILLAR 1 — THE MAP */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 5.7
              }} className="relative p-6 sm:p-8 bg-gradient-to-br from-cyan-950/30 to-black/60 border-2 border-cyan-500/50 rounded-xl shadow-[0_0_30px_rgba(6,182,212,0.15)]" data-id="element-2029">
                  {/* Pillar number badge */}
                  <div className="absolute -top-4 left-6 sm:left-8" data-id="element-2030">
                    <div className="px-4 py-1.5 bg-gradient-to-r from-cyan-600 to-cyan-500 rounded-full text-black text-xs sm:text-sm font-bold shadow-lg" data-id="element-2031">
                      PILLAR 1
                    </div>
                  </div>
                  <div className="mt-2" data-id="element-2032">
                    <h4 className="text-xl sm:text-2xl font-bold text-cyan-300 mb-1" style={{
                    textShadow: '0 0 20px rgba(6, 182, 212, 0.3)'
                  }} data-id="element-2033">
                      Brenden Henry's Monthly Intelligence Drop
                    </h4>
                    <p className="text-amber-400 font-bold text-lg sm:text-xl mb-4 tracking-wide" style={{
                    fontFamily: 'Cinzel, serif'
                  }} data-id="element-2034">
                      — THE MAP
                    </p>
                    <p className="text-stone-300 text-sm sm:text-base leading-relaxed" data-id="element-2035">
                      Brenden operates at a level of understanding most people
                      never experience. He maps the complete landscape of what's
                      possible with peptides, shows you the latest research,
                      demonstrates Precision Cell in action, and shows you what
                      99% of practitioners miss.{' '}
                      <span className="text-cyan-300 font-semibold" data-id="element-2036">
                        This is where you gain mastery.
                      </span>
                    </p>
                  </div>
                </motion.div>

                {/* PILLAR 2 — THE ROUTE */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 5.85
              }} className="relative p-6 sm:p-8 bg-gradient-to-br from-emerald-950/30 to-black/60 border-2 border-emerald-500/50 rounded-xl shadow-[0_0_30px_rgba(16,185,129,0.15)]" data-id="element-2037">
                  <div className="absolute -top-4 left-6 sm:left-8" data-id="element-2038">
                    <div className="px-4 py-1.5 bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-full text-black text-xs sm:text-sm font-bold shadow-lg" data-id="element-2039">
                      PILLAR 2
                    </div>
                  </div>
                  <div className="mt-2" data-id="element-2040">
                    <h4 className="text-xl sm:text-2xl font-bold text-emerald-300 mb-1" style={{
                    textShadow: '0 0 20px rgba(16, 185, 129, 0.3)'
                  }} data-id="element-2041">
                      Dr. Ali Mazloum's Live Strategic Briefing
                    </h4>
                    <p className="text-amber-400 font-bold text-lg sm:text-xl mb-4 tracking-wide" style={{
                    fontFamily: 'Cinzel, serif'
                  }} data-id="element-2042">
                      — THE ROUTE
                    </p>
                    <p className="text-stone-300 text-sm sm:text-base leading-relaxed" data-id="element-2043">
                      Clarity. Confidence. Direction. Life-changing insight. Ali
                      can take you from elite-level enhancement to
                      pathology-specific interventions, all the way to urgent
                      on-demand solutions.{' '}
                      <span className="text-emerald-300 font-semibold" data-id="element-2044">
                        This is where you gain the ability to know exactly what
                        to do in almost any situation.
                      </span>
                    </p>
                  </div>
                </motion.div>

                {/* PILLAR 3 — THE APPLICATION */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 6.0
              }} className="relative p-6 sm:p-8 bg-gradient-to-br from-amber-950/30 to-black/60 border-2 border-amber-500/50 rounded-xl shadow-[0_0_30px_rgba(217,119,6,0.15)]" data-id="element-2045">
                  <div className="absolute -top-4 left-6 sm:left-8" data-id="element-2046">
                    <div className="px-4 py-1.5 bg-gradient-to-r from-amber-600 to-amber-500 rounded-full text-black text-xs sm:text-sm font-bold shadow-lg" data-id="element-2047">
                      PILLAR 3
                    </div>
                  </div>
                  <div className="mt-2" data-id="element-2048">
                    <h4 className="text-xl sm:text-2xl font-bold text-amber-300 mb-1" style={{
                    textShadow: '0 0 20px rgba(251, 191, 36, 0.3)'
                  }} data-id="element-2049">
                      The Double Live HOTSEAT Q&A
                    </h4>
                    <p className="text-amber-400 font-bold text-lg sm:text-xl mb-4 tracking-wide" style={{
                    fontFamily: 'Cinzel, serif'
                  }} data-id="element-2050">
                      — THE APPLICATION
                    </p>
                    <p className="text-stone-300 text-sm sm:text-base leading-relaxed" data-id="element-2051">
                      Both Brenden and Dr. Ali team up live to answer YOUR
                      questions and give you exact directions for your
                      situation. It normally costs{' '}
                      <span className="text-amber-300 font-semibold" data-id="element-2052">
                        $1,500
                      </span>{' '}
                      to access ONE of them for an hour.{' '}
                      <span className="text-amber-300 font-semibold" data-id="element-2053">
                        Our last Q&A session with BOTH of them went for over
                        FOUR hours.
                      </span>
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Bold Summary Line */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 6.15
            }} className="mt-8 sm:mt-10 text-center max-w-3xl mx-auto" data-id="element-2054">
                <div className="p-5 sm:p-6 bg-gradient-to-r from-amber-950/40 via-amber-900/20 to-amber-950/40 border border-amber-500/40 rounded-lg" data-id="element-2055">
                  <p className="text-lg sm:text-xl md:text-2xl font-bold text-amber-300 leading-relaxed" style={{
                  textShadow: '0 0 20px rgba(251, 191, 36, 0.3)'
                }} data-id="element-2056">
                    Brenden gives you the complete map, Ali gives you the
                    precise route, and the live Q&A shows you how to apply both
                    to your specific situation.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* COSMIC TEXT SECTION - INSIDE OFFER BOX */}
            <div className="border-t-2 border-cyan-600/30 pt-8 sm:pt-10 mt-8 sm:mt-10" data-id="element-2057">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 1,
              delay: 6.05
            }} className="mb-8 sm:mb-10 max-w-5xl mx-auto relative" data-id="element-2058">
                {/* Dramatic spotlight behind text */}
                <div className="absolute inset-0 -inset-x-32 bg-gradient-radial from-cyan-600/10 via-transparent to-transparent blur-3xl" data-id="element-2059" />

                {/* Cosmic background - NO BOX, just floating elements */}
                <div className="relative py-8 sm:py-12 px-4 sm:px-6 overflow-visible" data-id="element-2060">
                  {/* Animated Stars - floating freely */}
                  <div className="absolute inset-0 -inset-x-20 -inset-y-20 overflow-visible pointer-events-none" data-id="element-2061">
                    {[...Array(40)].map((_, i) => <motion.div key={i} className="absolute bg-cyan-300 rounded-full" style={{
                    width: Math.random() * 3 + 1 + 'px',
                    height: Math.random() * 3 + 1 + 'px',
                    top: Math.random() * 120 - 10 + '%',
                    left: Math.random() * 120 - 10 + '%',
                    boxShadow: '0 0 4px rgba(103, 232, 249, 0.6)'
                  }} animate={{
                    opacity: [0.2, 0.7, 0.2],
                    scale: [0.8, 1.3, 0.8]
                  }} transition={{
                    duration: Math.random() * 3 + 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: Math.random() * 2
                  }} data-id="element-2062" />)}
                  </div>

                  {/* Constellation Lines - subtle connections */}
                  <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20 overflow-visible" data-id="element-2063">
                    <motion.path d="M100,30 L250,90 L400,50 L600,100" stroke="#22d3ee" strokeWidth="1" fill="none" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} animate={{
                    pathLength: 1,
                    opacity: 0.3
                  }} transition={{
                    duration: 5,
                    ease: 'easeOut',
                    delay: 1
                  }} data-id="element-2064" />
                    <motion.path d="M50,100 L200,40 L350,110 L550,60" stroke="#67e8f9" strokeWidth="1" fill="none" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} animate={{
                    pathLength: 1,
                    opacity: 0.25
                  }} transition={{
                    duration: 6,
                    ease: 'easeOut',
                    delay: 2
                  }} data-id="element-2065" />
                  </svg>

                  {/* The Cosmic Text - NO BOX, bold font, floating in space */}
                  <div className="relative z-10 text-center space-y-4 sm:space-y-6" data-id="element-2066">
                    <motion.p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-relaxed" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 2,
                    delay: 0.5
                  }} data-id="element-2067">
                      <span className="block text-cyan-200 font-extrabold" style={{
                      textShadow: '0 0 15px rgba(103, 232, 249, 0.5), 0 0 30px rgba(103, 232, 249, 0.3)'
                    }} data-id="element-2068">
                        Gain access to whole new worlds,
                      </span>
                      <span className="block mt-4 text-cyan-100 font-extrabold" style={{
                      textShadow: '0 0 18px rgba(34, 211, 238, 0.6), 0 0 35px rgba(34, 211, 238, 0.4)'
                    }} data-id="element-2069">
                        different dimensions of thinking,
                      </span>
                      <span className="block mt-4 text-white font-extrabold" style={{
                      textShadow: '0 0 20px rgba(255, 255, 255, 0.5), 0 0 40px rgba(103, 232, 249, 0.4)'
                    }} data-id="element-2070">
                        and new paradigms for medicine,
                      </span>
                      <span className="block mt-4 text-cyan-200 font-extrabold" style={{
                      textShadow: '0 0 15px rgba(103, 232, 249, 0.5), 0 0 30px rgba(103, 232, 249, 0.3)'
                    }} data-id="element-2071">
                        biology and peptides
                      </span>
                    </motion.p>
                  </div>

                  {/* Shooting Stars - multiple for more cosmic feel */}
                  <motion.div className="absolute top-10 left-0 h-px w-32 bg-gradient-to-r from-transparent via-cyan-300 to-transparent" style={{
                  rotate: '25deg',
                  filter: 'drop-shadow(0 0 8px #22d3ee)'
                }} animate={{
                  x: ['-100%', '400%'],
                  y: ['-100%', '400%'],
                  opacity: [0, 1, 0]
                }} transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  repeatDelay: 8,
                  ease: 'easeOut'
                }} data-id="element-2072" />
                  <motion.div className="absolute bottom-20 right-0 h-px w-24 bg-gradient-to-r from-transparent via-cyan-400 to-transparent" style={{
                  rotate: '-30deg',
                  filter: 'drop-shadow(0 0 6px #67e8f9)'
                }} animate={{
                  x: ['100%', '-400%'],
                  y: ['100%', '-400%'],
                  opacity: [0, 1, 0]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatDelay: 12,
                  ease: 'easeOut',
                  delay: 4
                }} data-id="element-2073" />
                </div>
              </motion.div>
            </div>

            {/* BUT MAKE SURE YOU UNDERSTAND Section - MOVED HERE */}

            {/* OR IF YOU'RE READY Section */}
            <div className="border-t-2 border-green-600/30 pt-8 sm:pt-10 mt-8 sm:mt-10" data-id="element-2074">
              {/* Elizabeth M. Testimonial */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 6.25
            }} className="relative mb-16" data-id="element-2075">
                {/* Subtle background glow - purple tint */}
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2076" />

                <div className="relative px-8 py-10" data-id="element-2077">
                  {/* Profile Image */}
                  <div className="flex justify-center mb-6" data-id="element-2078">
                    <div className="relative" data-id="element-2079">
                      <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-2080" />
                      <img src="https://cdn.magicpatterns.com/uploads/7z7kG6cfruhkmzvLk7oX76/image09.jpg" alt="Elizabeth M." className="relative w-24 h-24 rounded-full object-cover border-4 border-purple-500/40 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-2081" />
                    </div>
                  </div>

                  {/* Large opening quote - purple */}
                  <div className="mb-6" data-id="element-2082">
                    <span className="text-purple-500/40 text-7xl font-serif leading-none" data-id="element-2083">
                      "
                    </span>
                  </div>

                  {/* TV Screen Frame around testimonial text */}
                  <div className="relative mb-8 max-w-3xl mx-auto" data-id="element-2084">
                    {/* Outer purple glow */}
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2085" />

                    {/* TV screen frame */}
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2086">
                      {/* Screen scanline effect (subtle) */}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2087" />

                      {/* Testimonial text - with emphasis on key phrases */}
                      <p className="text-stone-300 text-lg md:text-xl leading-relaxed relative z-10" data-id="element-2088">
                        I've seen Brenden dissect complex pharmacological
                        mechanisms and explain them in a way that makes sense to
                        both medical professionals and laypeople alike.
                        <br data-id="element-2089" />
                        <br data-id="element-2090" />
                        He's able to{' '}
                        <span className="text-amber-400 font-semibold" data-id="element-2091">
                          predict outcomes and potential applications that
                          others simply don't see.
                        </span>
                        <br data-id="element-2092" />
                        <br data-id="element-2093" />
                        He's not afraid to challenge conventional wisdom when
                        the science doesn't support it. In a field often clouded
                        by hype and misinformation,{' '}
                        <span className="text-white font-bold" data-id="element-2094">
                          Brenden's dedication to truth is refreshing and
                          invaluable.
                        </span>
                        <br data-id="element-2095" />
                        <br data-id="element-2096" />
                        As the director of ReadyMed, I've worked with countless
                        physicians, hormone specialists, and pharmacists over
                        the years. But I can say without hesitation that{' '}
                        <span className="text-amber-400 font-semibold" data-id="element-2097">
                          Brenden Henry's knowledge of peptides, pharmacology,
                          and human biology is truly in a league of its own.
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Attribution with client type */}
                  <div className="flex items-center gap-4" data-id="element-2098">
                    <div className="h-px flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2099" />
                    <div className="text-right" data-id="element-2100">
                      <p className="text-purple-400 font-semibold text-lg mb-1" data-id="element-2101">
                        Elizabeth N.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-2102">
                        Director at ReadyMed, Peptide Mastery buyer
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Michelle M.N. Testimonial */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 6.9
            }} className="relative mt-16" data-id="element-2103">
                {/* Subtle background glow - purple tint */}
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2104" />
              </motion.div>
            </div>

            {/* SOPHISTICATED PRICING SECTION WITH STRONG ANCHORS */}
            <div className="border-t-2 border-amber-600/30 pt-8 sm:pt-10 mt-8 sm:mt-10" data-id="element-2105">
              {/* AMPLIFIED FOUNDING MEMBER MESSAGING */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: 6.5
            }} className="text-center mb-12 relative" data-id="element-2106">
                {/* Dramatic glow effect */}
                <div className="absolute inset-0 bg-gradient-radial from-amber-600/20 via-transparent to-transparent blur-3xl" data-id="element-2107" />

                <div className="relative space-y-4 sm:space-y-6" data-id="element-2108">
                  {/* Main headline with intense emphasis */}
                  <motion.div animate={{
                  textShadow: ['0 0 40px rgba(251, 191, 36, 0.6)', '0 0 60px rgba(251, 191, 36, 0.9)', '0 0 40px rgba(251, 191, 36, 0.6)']
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-2109">
                    <h3 className="text-2xl sm:text-4xl md:text-5xl font-bold text-amber-400 mb-3 sm:mb-4" data-id="element-2110">
                      🔥 PRO MEMBER EXCLUSIVE 🔥
                    </h3>
                  </motion.div>

                  <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4" data-id="element-2111">
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-100 leading-tight" data-id="element-2112">
                      This is one of the{' '}
                      <span className="text-amber-400" data-id="element-2113">most insane offers</span>{' '}
                      we will ever make
                    </p>

                    <p className="text-lg sm:text-xl text-stone-300 leading-relaxed" data-id="element-2114">
                      We're opening the doors to our{' '}
                      <span className="font-bold text-stone-100" data-id="element-2115">
                        Private Research Order
                      </span>{' '}
                      — and looking for the{' '}
                      <span className="font-bold text-stone-100" data-id="element-2116">
                        absolute best people
                      </span>{' '}
                      to join as PRO members.
                    </p>

                    <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-2117">
                      Lock in your{' '}
                      <span className="text-green-400" data-id="element-2118">
                        grandfathered PRO Member rate
                      </span>{' '}
                      now — and keep it for life.
                    </p>

                    <p className="text-base sm:text-lg text-stone-400 italic" data-id="element-2119">
                      (As long as you remain a member, your rate never
                      increases)
                    </p>
                  </div>

                  {/* Celebratory Trophy Animation */}
                  <motion.div initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.6,
                  delay: 6.55
                }} className="my-8 flex justify-center" data-id="element-2120">
                    <div className="relative w-48 h-32 md:w-64 md:h-40" data-id="element-2121">
                      {/* Trophy/Award Icon */}
                      <motion.svg className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 md:w-24 md:h-24" viewBox="0 0 24 24" fill="none" animate={{
                      y: [0, -8, 0],
                      rotate: [0, 5, 0, -5, 0]
                    }} transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }} data-id="element-2122">
                        {/* Trophy base */}
                        <path d="M12 15C15.866 15 19 11.866 19 8V5H5V8C5 11.866 8.13401 15 12 15Z" fill="url(#trophy-gradient)" stroke="#f59e0b" strokeWidth="1.5" data-id="element-2123" />
                        {/* Trophy stem */}
                        <path d="M12 15V19M9 19H15M9 22H15" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" data-id="element-2124" />
                        {/* Trophy handles */}
                        <path d="M5 8H3C2.44772 8 2 7.55228 2 7V6C2 5.44772 2.44772 5 3 5H5" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" data-id="element-2125" />
                        <path d="M19 8H21C21.5523 8 22 7.55228 22 7V6C22 5.44772 21.5523 5 21 5H19" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" data-id="element-2126" />
                        <defs data-id="element-2127">
                          <linearGradient id="trophy-gradient" x1="12" y1="5" x2="12" y2="15" data-id="element-2128">
                            <stop offset="0%" stopColor="#fbbf24" data-id="element-2129" />
                            <stop offset="100%" stopColor="#f59e0b" data-id="element-2130" />
                          </linearGradient>
                        </defs>
                      </motion.svg>

                      {/* Animated Confetti Particles */}
                      {[...Array(12)].map((_, i) => <motion.div key={i} className="absolute" style={{
                      left: '50%',
                      top: '50%',
                      width: i % 3 === 0 ? '8px' : '6px',
                      height: i % 3 === 0 ? '8px' : '6px'
                    }} animate={{
                      x: [0, Math.cos(i * Math.PI * 2 / 12) * 80],
                      y: [0, Math.sin(i * Math.PI * 2 / 12) * 60],
                      rotate: [0, 360],
                      opacity: [1, 0]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.1,
                      ease: 'easeOut'
                    }} data-id="element-2131">
                          <div className={`w-full h-full rounded-full ${i % 4 === 0 ? 'bg-amber-400' : i % 4 === 1 ? 'bg-yellow-300' : i % 4 === 2 ? 'bg-amber-500' : 'bg-orange-400'}`} style={{
                        boxShadow: '0 0 8px currentColor'
                      }} data-id="element-2132" />
                        </motion.div>)}

                      {/* Sparkle Effects */}
                      {[...Array(6)].map((_, i) => <motion.div key={`sparkle-${i}`} className="absolute" style={{
                      left: `${20 + i * 15}%`,
                      top: `${30 + i % 2 * 20}%`
                    }} animate={{
                      scale: [0, 1, 0],
                      rotate: [0, 180],
                      opacity: [0, 1, 0]
                    }} transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: i * 0.3,
                      ease: 'easeInOut'
                    }} data-id="element-2133">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-amber-400" data-id="element-2134">
                            <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5L8 0Z" fill="currentColor" data-id="element-2135" />
                          </svg>
                        </motion.div>)}
                    </div>
                  </motion.div>

                  {/* Divider with emphasis */}
                  <div className="flex items-center justify-center gap-4 my-8" data-id="element-2136">
                    <div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-2137" />
                    <div className="w-4 h-4 bg-amber-500 rotate-45 animate-pulse" data-id="element-2138" />
                    <div className="w-32 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-2139" />
                  </div>

                  {/* THE "INSANE" PART Section - MOVED HERE */}
                  <motion.div initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.6,
                  delay: 5.9
                }} className="text-center mb-12" data-id="element-2140">
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-4" style={{
                    textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
                  }} data-id="element-2141">
                      🎁 THE "INSANE" PART
                    </h3>
                    <div className="w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto mb-8" data-id="element-2142" />

                    <div className="space-y-6 max-w-3xl mx-auto" data-id="element-2143">
                      <p className="text-lg font-bold text-stone-100 underline decoration-amber-400 decoration-2 underline-offset-4" data-id="element-2144">
                        If you are accepted into the Order we will grant you a
                        complete scholarship to Peptide Mastery 2.0.
                      </p>

                      <div className="p-6 bg-amber-950/30 border-2 border-amber-500/40 rounded-lg" data-id="element-2145">
                        <p className="text-lg text-stone-300 mb-2" data-id="element-2146">
                          This is the most comprehensive peptide education ever
                          created.
                        </p>
                        <p className="text-2xl font-bold text-amber-400" data-id="element-2147">
                          Normally, it's $2,500 (pay-in-full ONLY).
                        </p>
                        <p className="text-stone-400 mt-2" data-id="element-2148">
                          We don't offer payment plans.
                        </p>
                      </div>

                      <div className="space-y-3" data-id="element-2149">
                        <p className="text-2xl font-bold text-amber-300" data-id="element-2150">
                          But as a PRO Member?
                        </p>
                        <p className="text-3xl font-bold text-green-400" style={{
                        textShadow: '0 0 30px rgba(74, 222, 128, 0.5)'
                      }} data-id="element-2151">
                          You get it FREE.
                        </p>
                      </div>

                      <p className="text-lg text-stone-300" data-id="element-2152">
                        Plus you're locking in a grandfathered monthly rate that
                        we'll NEVER offer again.
                      </p>

                      <p className="text-stone-400 italic text-sm" data-id="element-2153">
                        (Once enrollment closes, this pricing disappears
                        forever.)
                      </p>
                    </div>
                  </motion.div>

                  <p className="text-2xl font-bold text-amber-400" data-pricing-section style={{
                  scrollMarginTop: '80px'
                }} data-id="element-2154">
                    Choose Your PRO Member Path Below:
                  </p>
                </div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-2155">
                {/* Option 1: Pay in Full — BEST VALUE (emphasized) */}
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 6.6
              }} className="relative" data-id="element-2156">
                  {/* Animated outer glow border */}
                  <motion.div className="absolute -inset-[3px] rounded-xl opacity-70" animate={{
                  boxShadow: ['0 0 25px rgba(34, 197, 94, 0.4), 0 0 50px rgba(217, 119, 6, 0.2)', '0 0 35px rgba(34, 197, 94, 0.6), 0 0 70px rgba(217, 119, 6, 0.3)', '0 0 25px rgba(34, 197, 94, 0.4), 0 0 50px rgba(217, 119, 6, 0.2)']
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} style={{
                  border: '3px solid rgba(34, 197, 94, 0.5)',
                  borderRadius: '0.75rem',
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(217, 119, 6, 0.05))'
                }} data-id="element-2157" />

                  <div className="relative p-6 sm:p-8 bg-gradient-to-br from-amber-900/40 via-green-950/20 to-black border-3 border-amber-500 rounded-xl shadow-[0_0_40px_rgba(217,119,6,0.3)] hover:shadow-[0_0_60px_rgba(217,119,6,0.5)] transition-all" data-id="element-2158">
                    {/* Best Value Badge — prominent and animated */}
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-10" data-id="element-2159">
                      <div className="px-5 sm:px-8 py-2.5 bg-gradient-to-r from-green-600 to-green-500 rounded-full text-white text-sm sm:text-base font-bold shadow-[0_4px_15px_rgba(34,197,94,0.4)] whitespace-nowrap" data-id="element-2160">
                        ⭐ MOST POPULAR OPTION ⭐
                      </div>
                    </div>

                    <div className="text-center mb-6 mt-6" data-id="element-2161">
                      <p className="text-amber-400 font-semibold text-base sm:text-lg mb-2" data-id="element-2162">
                        Option 1
                      </p>
                      <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-stone-100 mb-4 leading-tight" data-id="element-2163">
                        Peptide Mastery 2.0 + PSI PRO{' '}
                        <span className="underline decoration-green-400 decoration-2 underline-offset-4" data-id="element-2164">
                          Annual
                        </span>{' '}
                        Membership
                      </h4>

                      {/* STRONG PRICE ANCHOR */}
                      <div className="mb-6 p-4 bg-red-950/30 border border-red-500/30 rounded-lg" data-id="element-2165">
                        <p className="text-red-400 text-xs sm:text-sm font-semibold mb-1" data-id="element-2166">
                          Normal Value:
                        </p>
                        <div className="flex items-center justify-center gap-3" data-id="element-2167">
                          <span className="text-2xl sm:text-3xl font-bold text-red-400 line-through opacity-60" data-id="element-2168">
                            $20,500+
                          </span>
                        </div>
                      </div>

                      <div className="mb-4" data-id="element-2169">
                        <p className="text-green-400 text-xs sm:text-sm font-bold mb-2" data-id="element-2170">
                          Your PRO Member Price:
                        </p>
                        <div className="flex items-baseline justify-center gap-2" data-id="element-2171">
                          <span className="text-4xl sm:text-5xl font-bold text-amber-400" data-id="element-2172">
                            $2,490
                          </span>
                        </div>
                        <p className="text-green-400 font-bold text-base sm:text-lg mt-2" data-id="element-2173">
                          Save over $18,000!
                        </p>
                        <p className="text-stone-400 text-xs sm:text-sm mt-1" data-id="element-2174">
                          Annual payment
                        </p>
                      </div>
                    </div>

                    {/* ⚡ INSTANT ACCESS CALLOUT — Key differentiator */}
                    <div className="mb-6 p-4 sm:p-5 bg-gradient-to-r from-green-950/40 via-green-900/30 to-green-950/40 border-2 border-green-500/50 rounded-lg relative overflow-hidden" data-id="element-2175">
                      <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-green-400/10 to-green-500/5" data-id="element-2176" />
                      <div className="relative text-center space-y-2" data-id="element-2177">
                        <p className="text-green-400 font-bold text-base sm:text-lg flex items-center justify-center gap-2" data-id="element-2178">
                          <span className="text-xl" data-id="element-2179">⚡</span> INSTANT FULL
                          ACCESS <span className="text-xl" data-id="element-2180">⚡</span>
                        </p>
                        <p className="text-stone-200 text-sm sm:text-base font-semibold" data-id="element-2181">
                          All{' '}
                          <span className="text-green-300 font-bold" data-id="element-2182">
                            200 modules
                          </span>{' '}
                          of Peptide Mastery 2.0 unlocked{' '}
                          <span className="text-green-300 font-bold underline decoration-green-400 underline-offset-2" data-id="element-2183">
                            immediately
                          </span>{' '}
                          upon enrollment
                        </p>
                        <p className="text-stone-400 text-xs sm:text-sm italic" data-id="element-2184">
                          No waiting. No drip. The entire course is yours from
                          day one.
                        </p>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-8" data-id="element-2185">
                      <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2186">
                        <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2187">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2188" />
                        </svg>
                        <span data-id="element-2189">
                          Full Peptide Mastery 2.0 —{' '}
                          <span className="font-bold text-green-300" data-id="element-2190">
                            ALL 200 modules unlocked immediately
                          </span>{' '}
                          <span className="text-stone-400" data-id="element-2191">($2,500 value)</span>
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2192">
                        <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2193">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2194" />
                        </svg>
                        <span data-id="element-2195">
                          12 months of PSI PRO membership{' '}
                          <span className="text-stone-400" data-id="element-2196">($3,000 value)</span>
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2197">
                        <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2198">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2199" />
                        </svg>
                        <span data-id="element-2200">All live sessions, bonuses, case studies</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2201">
                        <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2202">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2203" />
                        </svg>
                        <span data-id="element-2204">
                          <span className="font-semibold text-green-400" data-id="element-2205">
                            Save $498
                          </span>{' '}
                          vs monthly plan
                        </span>
                      </li>
                    </ul>

                    <a href="https://go.peptidescienceinstitute.com/pm-pro-annual-fm" className="block w-full px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black text-center font-bold text-base md:text-lg rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 min-h-[44px]" data-id="element-2206">
                      Apply Now — Annual Membership
                    </a>
                  </div>
                </motion.div>

                {/* Option 2: Monthly Payments */}
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 6.7
              }} className="relative p-6 sm:p-8 bg-gradient-to-br from-stone-900/60 to-black border-2 border-amber-500/40 rounded-xl shadow-[0_0_20px_rgba(217,119,6,0.15)] hover:shadow-[0_0_40px_rgba(217,119,6,0.3)] transition-all" data-id="element-2207">
                  <div className="text-center mb-6" data-id="element-2208">
                    <p className="text-amber-400/80 font-semibold text-base sm:text-lg mb-2" data-id="element-2209">
                      Option 2
                    </p>
                    <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-stone-100 mb-4 leading-tight" data-id="element-2210">
                      Peptide Mastery 2.0 + PSI PRO{' '}
                      <span className="underline" data-id="element-2211">Monthly</span> Membership
                    </h4>

                    {/* STRONG PRICE ANCHOR */}
                    <div className="mb-6 p-4 bg-red-950/30 border border-red-500/30 rounded-lg" data-id="element-2212">
                      <p className="text-red-400 text-xs sm:text-sm font-semibold mb-1" data-id="element-2213">
                        Normal Value:
                      </p>
                      <div className="flex items-center justify-center gap-3" data-id="element-2214">
                        <span className="text-2xl sm:text-3xl font-bold text-red-400 line-through opacity-60" data-id="element-2215">
                          $20,500+
                        </span>
                      </div>
                    </div>

                    <div className="mb-4" data-id="element-2216">
                      <p className="text-green-400 text-xs sm:text-sm font-bold mb-2" data-id="element-2217">
                        Your PRO Member Price:
                      </p>
                      <div className="flex items-baseline justify-center gap-2" data-id="element-2218">
                        <span className="text-4xl sm:text-5xl font-bold text-amber-400" data-id="element-2219">
                          $249
                        </span>
                        <span className="text-2xl text-stone-400" data-id="element-2220">today</span>
                      </div>
                      <p className="text-stone-300 font-medium text-base mt-2" data-id="element-2221">
                        Then 11 monthly payments of $249
                      </p>
                      <p className="text-green-400 font-bold text-sm mt-1" data-id="element-2222">
                        Save over $17,500!
                      </p>
                      <p className="text-stone-400 text-xs sm:text-sm mt-2" data-id="element-2223">
                        Total: $2,988
                      </p>
                    </div>
                  </div>

                  {/* Monthly drip note */}
                  <div className="mb-6 p-3 sm:p-4 bg-amber-950/20 border border-amber-500/20 rounded-lg" data-id="element-2224">
                    <div className="text-center space-y-1.5" data-id="element-2225">
                      <p className="text-amber-300/90 font-semibold text-sm sm:text-base" data-id="element-2226">
                        📚 Peptide Mastery 2.0 — Monthly Access
                      </p>
                      <p className="text-stone-400 text-xs sm:text-sm" data-id="element-2227">
                        Course modules are released in{' '}
                        <span className="text-stone-300 font-medium" data-id="element-2228">
                          12 monthly installments
                        </span>{' '}
                        matching your payment schedule
                      </p>
                      <p className="text-stone-500 text-xs italic" data-id="element-2229">
                        (Want all 200 modules immediately? Choose the Annual
                        option)
                      </p>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8" data-id="element-2230">
                    <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2231">
                      <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2232">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2233" />
                      </svg>
                      <span data-id="element-2234">
                        Peptide Mastery 2.0 —{' '}
                        <span className="font-semibold text-amber-300" data-id="element-2235">
                          unlocked in 12 monthly modules
                        </span>{' '}
                        <span className="text-stone-400" data-id="element-2236">($2,500 value)</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2237">
                      <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2238">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2239" />
                      </svg>
                      <span data-id="element-2240">
                        12 months of PSI PRO membership{' '}
                        <span className="text-stone-400" data-id="element-2241">($3,000 value)</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2242">
                      <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2243">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2244" />
                      </svg>
                      <span data-id="element-2245">All live sessions, bonuses, case studies</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2246">
                      <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2247">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2248" />
                      </svg>
                      <span data-id="element-2249">
                        <span className="font-semibold text-green-400" data-id="element-2250">
                          Lower upfront cost
                        </span>{' '}
                        — start for just $249
                      </span>
                    </li>
                  </ul>

                  <a href="https://go.peptidescienceinstitute.com/pm-pro-monthly-fm" className="block w-full px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black text-center font-bold text-base md:text-lg rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 min-h-[44px]" data-id="element-2251">
                    Apply Now — Monthly Membership
                  </a>
                </motion.div>
              </div>

              {/* Option 3: Alumni */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 6.8
            }} className="p-6 sm:p-8 bg-gradient-to-r from-purple-950/40 to-black border-2 border-purple-600/50 rounded-lg text-center" data-id="element-2252">
                <p className="text-purple-300 font-bold text-lg sm:text-xl mb-2" data-id="element-2253">
                  Already Own Peptide Mastery?
                </p>
                <p className="text-purple-200 mb-4 text-sm sm:text-base" data-id="element-2254">
                  Get special alumni pricing on the membership
                </p>
                <a href="mailto:support@support.peptidescienceinstitute.com" className="inline-block px-6 sm:px-8 py-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-lg transition-all duration-300 min-h-[44px]" data-id="element-2255">
                  Contact Us for Alumni Pricing
                </a>
              </motion.div>

              {/* BUT MAKE SURE YOU UNDERSTAND Section - MOVED HERE */}

              {/* THE DEADLINE Section */}
              <div className="border-t-2 border-amber-600/30 pt-8 sm:pt-10 mt-8 sm:mt-10" data-id="element-2256">
                <motion.div initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.6,
                delay: 6.85
              }} className="text-center mb-6 sm:mb-8" data-id="element-2257">
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-4" style={{
                  textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
                }} data-id="element-2258">
                    ⏰ THE DEADLINE
                  </h3>
                  <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto" data-id="element-2259" />
                </motion.div>

                <div className="space-y-4 sm:space-y-6 max-w-3xl mx-auto text-center" data-id="element-2260">
                  <div className="p-6 sm:p-8 bg-gradient-to-r from-red-950/40 via-amber-950/40 to-red-950/40 border-2 border-amber-500/50 rounded-lg" data-id="element-2261">
                    <p className="text-xl sm:text-2xl font-bold text-amber-300 mb-3 sm:mb-4" data-id="element-2262">
                      This opportunity is only open until Saturday, March 28th
                      at 11:59 PM ET
                    </p>
                    <p className="text-lg sm:text-xl text-stone-300 mb-2" data-id="element-2263">
                      After that, the doors close.
                    </p>
                    <p className="text-lg sm:text-xl text-stone-300 mb-2" data-id="element-2264">
                      Enrollment closes and pricing goes up.
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-red-400" data-id="element-2265">
                      And the price goes up.
                    </p>
                  </div>
                </div>
              </div>

              {/* Michelle M.N. Testimonial */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 6.9
            }} className="relative mt-8" data-id="element-2266">
                {/* Subtle background glow - purple tint */}
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2267" />

                <div className="relative px-8 py-10" data-id="element-2268">
                  {/* Profile Image */}
                  <div className="flex justify-center mb-6" data-id="element-2269">
                    <div className="relative" data-id="element-2270">
                      <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-2271" />
                      <img src="https://cdn.magicpatterns.com/uploads/9qMkPazyxjG9vTCAeui9mc/MichelleMN.jpg" alt="Michelle M.N." className="relative w-24 h-24 rounded-full object-cover border-4 border-purple-500/40 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-2272" />
                    </div>
                  </div>

                  {/* Large opening quote - purple */}
                  <div className="mb-6" data-id="element-2273">
                    <span className="text-purple-500/40 text-7xl font-serif leading-none" data-id="element-2274">
                      "
                    </span>
                  </div>

                  {/* TV Screen Frame around testimonial text */}
                  <div className="relative mb-8 max-w-3xl mx-auto" data-id="element-2275">
                    {/* Outer purple glow */}
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2276" />

                    {/* TV screen frame */}
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2277">
                      {/* Screen scanline effect (subtle) */}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2278" />

                      {/* Testimonial text - with emphasis on key phrases */}
                      <p className="text-stone-300 text-lg md:text-xl leading-relaxed relative z-10" data-id="element-2279">
                        I'm going through the peptide mastery and all the other
                        peptide courses and resources I've purchased from you
                        guys{' '}
                        <span className="text-amber-400 font-semibold" data-id="element-2280">
                          (I am LOVING it all!!)
                        </span>
                        ... I'm a functional medicine practitioner and{' '}
                        <span className="text-white font-bold" data-id="element-2281">
                          I can't wait to incorporate some of these protocols
                          with my patients!!!
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Attribution with client type */}
                  <div className="flex items-center gap-4" data-id="element-2282">
                    <div className="h-px flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2283" />
                    <div className="text-right" data-id="element-2284">
                      <p className="text-purple-400 font-semibold text-lg mb-1" data-id="element-2285">
                        Michelle M.N.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-2286">
                        Doctor of Chiropractic & Certified Functional Medicine
                        Practitioner, Peptide Mastery buyer
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Tony R. Testimonial */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 7.1
            }} className="relative mt-16" data-id="element-2287">
                {/* Subtle background glow - purple tint */}
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2288" />

                <div className="relative px-8 py-10" data-id="element-2289">
                  {/* Profile Image */}
                  <div className="flex justify-center mb-6" data-id="element-2290">
                    <div className="relative" data-id="element-2291">
                      <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-2292" />
                      <img src="https://cdn.magicpatterns.com/uploads/p7h6Y7wV2aZ8GFLXspCfFF/TonyR.jpg" alt="Tony R." className="relative w-24 h-24 rounded-full object-cover border-4 border-purple-500/40 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-2293" />
                    </div>
                  </div>

                  {/* Large opening quote - purple */}
                  <div className="mb-6" data-id="element-2294">
                    <span className="text-purple-500/40 text-7xl font-serif leading-none" data-id="element-2295">
                      "
                    </span>
                  </div>

                  {/* TV Screen Frame around testimonial text */}
                  <div className="relative mb-8 max-w-3xl mx-auto" data-id="element-2296">
                    {/* Outer purple glow */}
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2297" />

                    {/* TV screen frame */}
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2298">
                      {/* Screen scanline effect (subtle) */}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2299" />

                      {/* Testimonial text - with emphasis on key phrases */}
                      <p className="text-stone-300 text-lg md:text-xl leading-relaxed relative z-10" data-id="element-2300">
                        I was honestly frustrated at first. All the questions,
                        testing and monitoring seemed excessive. But I
                        understand why it was so important. Everything gave you
                        information to fine-tune my program... What you have
                        managed to do is{' '}
                        <span className="text-amber-400 font-semibold" data-id="element-2301">
                          absolutely bonkers.
                        </span>{' '}
                        This is like a{' '}
                        <span className="text-white font-bold" data-id="element-2302">
                          whole different planet of health optimization that
                          most people don't even know exists
                        </span>
                        ... I've gone from{' '}
                        <span className="text-amber-400 font-semibold" data-id="element-2303">
                          thinking about my funeral to planning hiking trips.
                        </span>{' '}
                        I can't thank you guys and the team enough.
                      </p>
                    </div>
                  </div>

                  {/* Attribution with client type */}
                  <div className="flex items-center gap-4" data-id="element-2304">
                    <div className="h-px flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2305" />
                    <div className="text-right" data-id="element-2306">
                      <p className="text-purple-400 font-semibold text-lg mb-1" data-id="element-2307">
                        Tony R.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-2308">
                        Transformation Services Client
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* TRANSITION SECTION - "WANT THE FULL DETAILS?" */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 6.9
      }} className="mt-12 sm:mt-20 mb-10 sm:mb-16 text-center max-w-3xl mx-auto" data-id="element-2309">
          {/* Scroll icon */}
          <motion.div className="flex justify-center mb-6" animate={{
          y: [0, 8, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-2310">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 border-2 border-amber-500/40 flex items-center justify-center" data-id="element-2311">
              <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-2312">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 13l-7 7-7-7m14-8l-7 7-7-7" data-id="element-2313" />
              </svg>
            </div>
          </motion.div>

          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-4 sm:mb-6" style={{
          textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
        }} data-id="element-2314">
            📜 WANT THE FULL DETAILS?
          </h3>

          <div className="space-y-3 sm:space-y-4 text-stone-300" data-id="element-2315">
            <p className="text-lg sm:text-xl font-semibold" data-id="element-2316">
              Keep reading below.
            </p>

            <p className="text-base sm:text-lg" data-id="element-2317">
              I'm going to walk you through EXACTLY what you're getting...
            </p>

            <p className="text-base sm:text-lg" data-id="element-2318">
              Why this matters more now than ever...
            </p>

            <p className="text-base sm:text-lg font-bold text-amber-400" data-id="element-2319">
              And why this might be the most important decision you make all
              year.
            </p>
          </div>

          {/* Decorative divider */}
          <div className="flex items-center justify-center gap-4 mt-10" data-id="element-2320">
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-2321" />
            <div className="w-2 h-2 bg-amber-500 rounded-full" data-id="element-2322" />
            <div className="w-32 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-2323" />
          </div>
        </motion.div>

        {/* Enhanced scroll indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 5.0
      }} className="mt-16" data-id="element-2324">
          <div className="flex flex-col items-center gap-3" data-id="element-2325">
            <p className="text-amber-500 text-sm tracking-wider uppercase" data-id="element-2326">
              Discover More
            </p>
            <motion.div className="w-px h-16 bg-gradient-to-b from-amber-600 to-transparent" animate={{
            opacity: [0.3, 1, 0.3]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-2327" />
            <motion.div animate={{
            y: [0, 10, 0]
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-2328">
              <ChevronDown className="w-6 h-6 text-amber-600" data-id="element-2329" />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}